/**
 * API service — talks to the Bandz Express+SQLite backend (port 3099 in production).
 * All data is persisted server-side.
 *
 * Band endpoints:
 *   GET    /api/bands
 *   GET    /api/bands/:id
 *   POST   /api/bands
 *   PUT    /api/bands/:id
 *   DELETE /api/bands/:id
 * Rider (rider = rider config + musicians + stageplots):
 *   GET    /api/bands/:id/rider
 *   PUT    /api/bands/:id/rider
 *   GET/PUT /api/bands/:id/rider/config | /musicians | /stageplots
 * Tour endpoints:
 *   GET /api/venues, /api/contacts, /api/tours, /api/shows,
 *       /api/calendar, /api/stats
 *   GET/PUT/POST/DELETE /api/bands/:id/setlists
 */

import type { DaysheetLayout } from './lib/daysheetLayout';
import type { TourBookLayout } from './lib/tourbookLayout';

const API_BASE = '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`API ${status}: ${typeof body === 'string' ? body : JSON.stringify(body)}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// Called when any API returns 401 (except login/me) so the app can bounce to /login.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

/* ------------------------------------------------------------------ *
 * Offline data layer (see src/lib/offline.ts for the full design).
 *
 * Every request below funnels through fetchJSON, which decides:
 *   - /api/auth/*       → always the real network (never cached, never queued)
 *   - offline mode      → GET reads the IndexedDB cache, mutations go into the
 *                         durable 'pending' queue with an optimistic response
 *   - online mode       → unchanged behaviour; successful GETs are cached,
 *                         and a network TypeError flips the app to offline and
 *                         retries the request through the offline path
 * ------------------------------------------------------------------ */
import {
  getOfflineState,
  setOfflineMode,
  isNetworkError,
  cachePut,
  cacheGet,
  enqueuePending,
  flushPending,
} from './lib/offline';
import { isShowcaseMode } from './lib/saveStatus';

/** Mutations the showcase (demo) account may still send: auth (login/logout)
 *  and the PDF export — a pure read/export endpoint that persists nothing. */
const SHOWCASE_ALLOWED_WRITES = ['/api/auth', '/api/pdf'];

/** POSTs that only READ band data — the copilot's read surfaces. They are the
 *  client-side mirror of the server exemption (`AI_READ_ONLY_PATHS` in
 *  server/index.js): /assist builds a prompt and returns a proposal, /chat and
 *  /chat/action answer questions. Nothing is written.
 *
 *  ⚠️ EXACT PATHS, never a prefix test: `/api/ai/chat` is a PREFIX of
 *  `/api/ai/chat/apply`, which WRITES the confirmed proposal. A `includes()`
 *  check would hand the demo a write path (the server still 403s it, but the
 *  client must not launch a condemned write either).
 *  ⚠️ Keep in sync with AI_READ_ONLY_PATHS — same three paths, both halves. */
const SHOWCASE_READ_LIKE_POSTS = ['/api/ai/assist', '/api/ai/chat', '/api/ai/chat/action', '/api/ai/stageplot-command'];
export function isShowcaseReadLikePost(url: string): boolean {
  const p = url.split('?')[0].replace(/^https?:\/\/[^/]+/, '');
  return SHOWCASE_READ_LIKE_POSTS.includes(p);
}
export class ShowcaseReadOnlyError extends Error {
  constructor() {
    super('Demo account — sample data, read-only');
    this.name = 'ShowcaseReadOnlyError';
  }
}

function isOfflineNow(): boolean {
  if (getOfflineState().offline) return true;
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** GETs we never cache: auth (except /auth/me — the current user's session,
 * used as proof-of-login for offline mode), health (connectivity probe). */
function shouldCacheUrl(url: string): boolean {
  if (url.includes('/api/health')) return false;
  if (!url.includes('/api/auth')) return true;
  return url.includes('/api/auth/me');
}

function parseBody(body: unknown): unknown {
  if (typeof body === 'string') {
    try { return JSON.parse(body); } catch { /* keep raw */ return body; }
  }
  return body;
}

/**
 * Simulated server response returned to the UI for a queued mutation, so the
 * optimistic update renders exactly like a real save. See the "Known
 * limitations" note in src/lib/offline.ts about POST ids.
 */
function optimisticResponse(method: string, url: string, body: unknown): unknown {
  const isObj = body !== null && typeof body === 'object' && !Array.isArray(body);
  const b = (isObj ? body : {}) as Record<string, unknown>;
  if (method === 'POST') {
    // The queued body carries the injected client id — reuse it so the UI's
    // optimistic object and the replayed payload agree.
    return { id: b.id ?? crypto.randomUUID(), ...b };
  }
  if (method === 'PUT' || method === 'PATCH') {
    const idFromUrl = url.split('/').filter(Boolean).pop();
    return isObj
      ? { ...b, ...(idFromUrl && b.id === undefined ? { id: idFromUrl } : {}) }
      : { ok: true };
  }
  return { success: true }; // DELETE
}

/** Real network request: current behaviour + GET result caching.
 *  Returns { data, headers } — the response headers are needed by the
 *  rider-list version handshake (`X-Rider-Version`, see apiGetMusiciansMeta). */
async function realFetchJSONMeta(url: string, options?: RequestInit): Promise<{ data: any; headers: Headers }> {
  // Merge caller headers ON TOP of the default Content-Type — spreading
  // `...options` wholesale used to REPLACE the headers object, dropping
  // Content-Type whenever a call added one (e.g. the config version header).
  const headers = { 'Content-Type': 'application/json', ...((options && options.headers) || {}) };
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers,
  });
  if (!res.ok) {
    if (
      res.status === 401 &&
      !url.includes('/api/auth/login') &&
      !url.includes('/api/auth/me') &&
      // PUBLIC share routes are mounted BEFORE the auth gate (rider, advancing,
      // promo kit): their 401 is a PAYLOAD answer, never an expired session.
      // The Promo kit link answers `401 { passwordRequired: true }` when a
      // password is set, and a visitor with no account must see the lock screen
      // — bouncing them to /login would make the page unreachable (2026-10-16).
      !url.includes('/api/share/')
    ) {
      // Session expired or not authenticated: send the user to the login page.
      if (onUnauthorized) onUnauthorized();
      else if (typeof window !== 'undefined') window.location.assign('/login');
    }
    const text = await res.text().catch(() => res.statusText);
    let body: unknown = text;
    try { body = JSON.parse(text); } catch { /* retain text */ }
    throw new ApiError(res.status, body);
  }
  const data = await res.json();
  const method = (options?.method ?? 'GET').toUpperCase();
  if (method === 'GET' && shouldCacheUrl(url)) {
    await cachePut(url, data); // cache failures are non-fatal
  }
  return { data, headers: res.headers };
}

/** Real network request: current behaviour + GET result caching. */
async function realFetchJSON(url: string, options?: RequestInit) {
  const { data } = await realFetchJSONMeta(url, options);
  return data;
}

/** Offline path: GET → cache, mutations → pending queue + optimistic reply. */
async function offlineFetchJSON(url: string, method: string, options?: RequestInit) {
  if (method === 'GET' || method === 'HEAD') {
    const cached = await cacheGet(url);
    if (cached !== undefined) return cached;
    throw new ApiError(503, {
      error: 'offline',
      message: 'You are offline and this data has not been cached yet.',
    });
  }
  const body = parseBody(options?.body);
  const record = await enqueuePending({ url, method, body });
  // Keep the GET cache in sync with the optimistic state: if the user reloads
  // while still offline, the mutated resource must come back from the cache
  // with the edits applied — otherwise an autosave started from the stale
  // cached state can queue a PUT that overwrites the edits in the pending
  // queue. Only URLs whose PUT body IS the full resource state are safe to
  // mirror into the cache (partial PUTs like /members/:id {role} would poison
  // the cached GET). The rider list endpoints are exactly this case.
  if (
    (method === 'PUT' || method === 'PATCH') &&
    isFullStateMutationUrl(url)
  ) {
    await cachePut(url, body);
  }
  return optimisticResponse(method, url, record.body);
}

/** True when a PUT/PATCH body is the full state of the GET at that URL, so it
 * is safe to mirror into the offline cache. Partial updates (e.g. only `role`)
 * must NOT overwrite the cached resource. */
function isFullStateMutationUrl(url: string): boolean {
  const u = url.split('?')[0];
  return (
    u.endsWith('/rider/musicians') ||
    u.endsWith('/rider/crew') ||
    u.endsWith('/rider/stageplots') ||
    u.endsWith('/rider/config') ||
    u.endsWith('/rider') ||
    u.endsWith('/team') ||
    u.endsWith('/setlists')
  );
}

async function fetchJSON(url: string, options?: RequestInit) {
  const method = (options?.method ?? 'GET').toUpperCase();

  // SHOWCASE (demo) account: strictly read-only. No mutation may leave the
  // client — the server refuses them anyway (403 read-only guard), but cutting
  // them here is what keeps the demo UI free of failed-save noise ("Not saved").
  // Exceptions: auth (login/logout), the PDF export (pure read/export) and the
  // copilot's read surfaces (/assist, /chat, /chat/action — mirror of the
  // server exemption). The copilot's /apply and /chat/apply stay blocked.
  if (
    isShowcaseMode() &&
    !['GET', 'HEAD', 'OPTIONS'].includes(method) &&
    !SHOWCASE_ALLOWED_WRITES.some((p) => url.includes(p)) &&
    !isShowcaseReadLikePost(url)
  ) {
    return Promise.reject(new ShowcaseReadOnlyError());
  }

  // Auth calls always hit the network: login must never come from cache and
  // credentials must never sit in the offline queue. EXCEPTION: /auth/me is a
  // harmless GET that carries the logged-in user — caching it lets the app stay
  // signed in (and show cached data) when the flight is offline.
  if (url.includes('/api/auth') && !url.includes('/api/auth/me')) {
    return realFetchJSON(url, options);
  }

  if (isOfflineNow()) {
    return offlineFetchJSON(url, method, options);
  }

  try {
    return await realFetchJSON(url, options);
  } catch (err) {
    // Network-level failure (fetch TypeError: DNS, connection refused, airplane
    // mode...): switch to offline mode and retry through the offline path so
    // this request still succeeds (cache read / pending queue).
    if (isNetworkError(err)) {
      setOfflineMode(true);
      return offlineFetchJSON(url, method, options);
    }
    throw err;
  }
}

/** Variant of `fetchJSON` that ALSO returns the response headers (or null when
 *  the reply came from the offline cache / queue, which carries no headers).
 *  Used for the rider-list version handshake so a GET can hand back
 *  `X-Rider-Version` and a PUT can echo it. Offline replies have null headers
 *  => the caller announces no version => the server never refuses a replayed
 *  save (same anti-wedge rule as the config `X-Config-Version` header). */
async function fetchJSONMeta(url: string, options?: RequestInit): Promise<{ data: any; headers: Headers | null }> {
  const method = (options?.method ?? 'GET').toUpperCase();

  if (
    isShowcaseMode() &&
    !['GET', 'HEAD', 'OPTIONS'].includes(method) &&
    !SHOWCASE_ALLOWED_WRITES.some((p) => url.includes(p)) &&
    !isShowcaseReadLikePost(url)
  ) {
    return Promise.reject(new ShowcaseReadOnlyError());
  }

  if (url.includes('/api/auth') && !url.includes('/api/auth/me')) {
    return realFetchJSONMeta(url, options);
  }

  if (isOfflineNow()) {
    const data = await offlineFetchJSON(url, method, options);
    return { data, headers: null };
  }

  try {
    return await realFetchJSONMeta(url, options);
  } catch (err) {
    if (isNetworkError(err)) {
      setOfflineMode(true);
      const data = await offlineFetchJSON(url, method, options);
      return { data, headers: null };
    }
    throw err;
  }
}

/** Read the numeric rider-list version from a response's `X-Rider-Version`.
 *  Returns null when absent/non-numeric (legacy server or offline cache). */
function riderVersionFromHeaders(headers: Headers | null): number | null {
  const raw = headers ? headers.get('X-Rider-Version') : null;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/* ----------------------------- Bands ----------------------------- */

export interface BandDTO {
  id: string;
  name: string;
  slug?: string;
  logo?: string;
  image_url?: string;
  genre?: string;
  country?: string;
  // Home base of the band (2026-10-11): the van's departure point for the
  // automatic « band -> venue » distance leg. lat/lng are filled server-side by
  // apiGeocodeBandAddress.
  address?: string;
  postal_code?: string;
  city?: string;
  lat?: number | null;
  /** Aéroport de préférence (2026-10-17) : défaut des travel days en mode plane. */
  default_airport?: string;
  /** Gare de préférence (2026-11-02, LOT U2c) : jambes train (code UIC). */
  default_station?: string;
  lng?: number | null;
  formed_year?: number;
  socials?: Record<string, string>;
  image?: string;
  color?: string;
  lastModified?: number;
  schemaVersion?: number;
  // The caller's role on this band ('owner' | 'editor' | 'viewer'), returned by
  // the API so the UI can hide actions the user is not allowed to perform.
  my_role?: 'owner' | 'editor' | 'viewer';
  // Whether the caller has FINANCE access on this band (owner/superadmin always
  // true; editor/viewer only if the owner granted finance_access). (2026-08-23)
  finance_access?: boolean;
  // Whether the caller has LAYOUT access on this band (owner/superadmin always
  // true; editor/viewer only if the owner granted layout_access) — gates the 3
  // PDF layout editors. Raw flag (0/1). LEGACY derived value (2026-09-24).
  layout_access?: number;
  // The caller's EFFECTIVE capability matrix on this band (chantier C,
  // 2026-09-25) — the delegable capabilities granted to the signed-in user.
  // This is what the sidebar, the page guards and the editors read (see
  // src/lib/permissions.ts). Absent on a pre-matrix payload.
  permissions?: string[];
  /** Read DENIES (`<family>.view` stored granted = 0) — the « — » level of the
      page×level grid (2026-10-08). Distinct from an ABSENT grant: absence keeps
      the historical behaviour (reads allowed). */
  denied?: string[];
  // Secret capability token for the band's public .ics calendar subscription
  // link. Only returned on authenticated, band-scoped calls — never on a public
  // endpoint. (2026-08-21)
  calendar_token?: string;
  // Editable public promo-page blob (option C, 2026-10-19) + the tour whose
  // content the public page shows by default. Public text — not a secret.
  promo_content?: string | null;
  promo_tour_id?: number | null;
}

export async function apiGetBands(): Promise<BandDTO[]> {
  return fetchJSON(`${API_BASE}/bands`);
}

export async function apiGetBand(id: string): Promise<BandDTO> {
  return fetchJSON(`${API_BASE}/bands/${id}`);
}

export interface BandFileDTO {
  id: number;
  band_id: string;
  show_id: number | null;
  category: string;
  filename: string;
  url: string;
  size: number;
  mime: string;
  uploaded_at: string;
}
export type BandFileCategory = 'files' | 'promo_kit' | 'light_plot';
export async function apiGetBandFiles(bandId: string, showId?: number | null, category: BandFileCategory = 'files'): Promise<BandFileDTO[]> {
  const q: string[] = [];
  if (showId) q.push(`show=${showId}`);
  if (category && category !== 'files') q.push(`category=${category}`);
  return fetchJSON(`${API_BASE}/bands/${bandId}/files${q.length ? `?${q.join('&')}` : ''}`);
}
export async function apiUploadBandFile(bandId: string, data: { filename: string; mime: string; base64: string; show_id?: number | null; category?: BandFileCategory }): Promise<BandFileDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/files`, { method: 'POST', body: JSON.stringify(data) });
}

/** Files at or below this size go through the legacy base64 JSON route; larger
 *  ones switch to the RAW route automatically (200 MB server cap, 2026-10-19).
 *  The base64 route stays capped by express.json (25 mb payload ⇒ ~18 MB file),
 *  which is exactly why the switch must happen at 20 MB. */
export const BAND_FILE_RAW_THRESHOLD = 20 * 1024 * 1024;

/** RAW upload: the body IS the file (application/octet-stream), metadata rides
 *  the query string. Direct `fetch` like `apiUploadImage` — a binary body must
 *  NOT enter the offline replay queue (enqueuePending JSON-serializes bodies).
 *  Same rights/validation as the base64 route server-side; no new privilege. */
export async function apiUploadBandFileRaw(bandId: string, file: File, opts: { show_id?: number | null; category?: BandFileCategory } = {}): Promise<BandFileDTO> {
  const q = new URLSearchParams({ filename: file.name, mime: file.type || '' });
  if (opts.category) q.set('category', opts.category);
  if (opts.show_id != null) q.set('show_id', String(opts.show_id));
  const res = await fetch(`${API_BASE}/bands/${bandId}/files/raw?${q.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    credentials: 'include',
    body: file,
  });
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = (j && j.error) || ''; } catch { /* not json */ }
    throw new Error(detail || `Upload failed (${res.status})`);
  }
  return res.json();
}
export async function apiDeleteBandFile(bandId: string, id: number): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/files/${id}`, { method: 'DELETE' });
}
/** URL of the guarded server-rendered preview (PDF page N → PNG; images are a
 *  passthrough with `page` ignored). `page` defaults to 1. Band members only —
 *  the browser sends the session cookie automatically for a same-origin <img
 *  src>. Never cache the URL string itself. (2026-10-06) */
export function apiBandFilePreviewUrl(bandId: string, fileId: number, page = 1): string {
  const base = `${API_BASE}/bands/${bandId}/files/${fileId}/preview`;
  return page > 1 ? `${base}?page=${page}` : base;
}
/** Page count of a stored light-plot file, read from the preview route's
 *  `X-Pdf-Pages` response header (the server's cached pdfinfo total for a PDF,
 *  1 for an image; 1 on any failure). Only the header is consumed — the body
 *  stream is released without buffering it. (2026-10-06) */
export async function apiBandFilePreviewPages(bandId: string, fileId: number): Promise<number> {
  try {
    const res = await fetch(apiBandFilePreviewUrl(bandId, fileId), { credentials: 'include' });
    const n = Number.parseInt(res.headers.get('X-Pdf-Pages') || '', 10);
    try { await res.body?.cancel(); } catch { /* body already released */ }
    return Number.isFinite(n) && n > 0 ? n : 1;
  } catch {
    return 1;
  }
}

// ---- Promo kit (Band Settings) ----
/**
 * Get the band's public Promo kit token (created if missing) AND the link's
 * password. Editor+ only. `password` is the DECRYPTED value ('' when none, and
 * also '' for a caller without the write right — the level that may READ it
 * back is the same level that may set it). (2026-10-16)
 */
export async function apiGetPromoToken(bandId: string): Promise<{ token: string; hasPassword: boolean; password: string }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/promo-token`);
}

/**
 * Set / change / clear the password of the band's public Promo kit link.
 * An empty string clears it. The public LINK (`token`) never changes.
 * (2026-10-16)
 */
export async function apiSetPromoPassword(
  bandId: string,
  password: string,
): Promise<{ hasPassword: boolean; password: string; token: string }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/promo-password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  });
}
/** Rotate the band's Promo kit token (the old public link stops working). */
export async function apiRegeneratePromoToken(bandId: string): Promise<{ token: string }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/promo-token`, { method: 'POST' });
}

// ---- Promo kit page CONTENT (option C, 2026-10-19) ----
/** A named download section of the public promo page. */
/** A mixed ordered item inside a promo section (2026-10-19, links + videos).
 *  `fileId` items are downloads; `link` items open http(s) URLs in a new tab;
 *  `video` items are YouTube-only, normalized server-side to a
 *  youtube-nocookie embed URL. */
export type PromoContentItem =
  | { type: 'file'; fileId: number }
  | { type: 'link'; label: string; url: string }
  | { type: 'video'; url: string };

export interface PromoContentSection {
  key: string;
  title: string;
  /** Legacy file-only view (kept for backcompat); the source of truth is `items`. */
  fileIds: number[];
  /** Mixed ordered items (files / links / videos). Legacy blobs are normalized. */
  items: PromoContentItem[];
  position: number;
  /** HIDDEN (2026-10-20): absent = visible (strict backcompat); true = the
   *  public page skips the section but the editor keeps it (grayed out). */
  hidden?: boolean;
}
/** The editable public promo page blob (bands.promo_content / tours.promo_content). */
export interface PromoContentDTO {
  tourTitle?: string;
  intro?: string;
  bio?: string;
  contact?: string;
  quotes: { text: string; source?: string }[];
  sections: PromoContentSection[];
  /** Hero image override (option B, 2026-10-19): absent = auto, 0 = no hero
   *  (gradient only), > 0 = this promo_kit image primes over the derivation. */
  heroImageFileId?: number;
  /** Social links (Sandro v2, 2026-10-19): each optional, http(s) only,
   *  whitelisted keys server-side; absent = no icon on the public page. */
  socials?: { instagram?: string; tiktok?: string; spotify?: string; youtube?: string; website?: string };
  /** BLOCK ORDER (2026-10-20): optional ordered list of content block ids
   *  (`intro`/`bio`/`quotes`/`socials`/`contact`/`section:<key>`); absent =
   *  canonical order (strict backcompat). The hero is never listed. */
  blockOrder?: string[];
  /** Present on the PUBLIC payload only: the tour behind the content. */
  tourId?: number;
  tourPeriod?: string;
}
/** Save the band's default promo page blob (null = clear → classic page). */
export async function apiPutBandPromoContent(bandId: string, content: PromoContentDTO | null): Promise<{ ok: boolean; content: PromoContentDTO | null }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/promo-content`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}
/** Choose which tour's content the public page shows (null = band fallback). */
export async function apiPutBandPromoSource(bandId: string, tourId: number | null): Promise<{ ok: boolean; promo_tour_id: number | null }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/promo-source`, {
    method: 'PUT',
    body: JSON.stringify({ tourId }),
  });
}
/** Read one tour's promo page blob (the editor's source switch loads it). */
export async function apiGetTourPromoContent(tourId: string | number): Promise<{ ok: boolean; content: PromoContentDTO | null }> {
  return fetchJSON(`${API_BASE}/tours/${encodeURIComponent(String(tourId))}/promo-content`, {
    method: 'GET',
  });
}
/** Save one tour's promo page blob (null = clear → band fallback). */
export async function apiPutTourPromoContent(tourId: string | number, content: PromoContentDTO | null): Promise<{ ok: boolean; content: PromoContentDTO | null }> {
  return fetchJSON(`${API_BASE}/tours/${encodeURIComponent(String(tourId))}/promo-content`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}

// ---- Daysheet reminders (per show, in the Day Sheet) ----
// Moved from Band Settings (per-band) to the show's Day Sheet on 2026-08-23.
/** A member who can be emailed for a show's daysheet reminder: active on THIS
 * show (rider active_musician_ids/active_crew_ids) and has an email. */
export interface ReminderMember {
  id: string;
  name: string;
  nickname: string;
  email: string;
}
/** Per-show daysheet reminder settings. `enabled` is 0 (off, default) or 1 (on). */
export interface ReminderSettings {
  show_id: string;
  enabled: number;
  /** Recipients: team_member ids and/or emails. Empty = no recipients. */
  recipients: string[];
  /** Send hour 0-23 (default 6 = 06:00). */
  reminder_hour: number;
  /** How many days BEFORE the show the reminder is sent: 0 = on the show day,
   * 1 = the day before (default), up to 7. */
  day_offset: number;
  /** The people the recipient picker offers: active members of this show
   * (not the whole band) who have an email. Empty when the show has no active
   * members or none of them have an email yet. Present on GET; the PUT
   * response only echoes the saved setting fields. */
  available_members?: ReminderMember[];
}
/** Payload sent to PUT /api/shows/:id/reminder-settings (editor+ on the show's band). */
export interface ReminderSettingsInput {
  enabled: number;
  recipients: string[];
  reminder_hour?: number;
  /** Days before the show: 0 = on the show day, 1 = the day before (default), ..7. */
  day_offset?: number;
}
/** Result of POST /api/shows/:id/reminder-settings/send (the "Send now" button). */
export interface ReminderSendResult {
  sent: boolean;
  recipients: string[];
  errors: string[];
}
/** Read a show's daysheet reminder settings (viewer+ on the show's band). */
export async function apiGetShowReminderSettings(showId: string): Promise<ReminderSettings> {
  return fetchJSON(`${API_BASE}/shows/${showId}/reminder-settings`);
}
/** Persist a show's daysheet reminder settings (editor+ on the show's band). */
export async function apiPutShowReminderSettings(showId: string, data: ReminderSettingsInput): Promise<ReminderSettings> {
  return fetchJSON(`${API_BASE}/shows/${showId}/reminder-settings`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
/** Send this show's daysheet email right now (editor+ on the show's band).
 * NETWORK-ONLY on purpose: unlike a normal mutation this one must never enter
 * the offline queue (a queued "Send now" would fire a real email, hours later,
 * with no user intent). Offline => a clear error the caller shows as-is. */
export async function apiSendShowReminderNow(showId: string): Promise<ReminderSendResult> {
  if (isOfflineNow()) {
    throw new Error('You are offline. Reconnect to send the reminder now.');
  }
  return realFetchJSON(`${API_BASE}/shows/${showId}/reminder-settings/send`, { method: 'POST' });
}

// ---- Per-show tasks ----
/** A to-do assigned to a team member, scoped to a single show. */
export interface ShowTask {
  id: number;
  show_id: number;
  title: string;
  /** team_member id the task is assigned to, or '' for unassigned. */
  assigned_to: string;
  /** Optional due date, 'YYYY-MM-DD' or ''. */
  due_date: string;
  status: 'todo' | 'in_progress' | 'done';
  created_by: string;
  created_at: string;
  updated_at: string;
}
export type ShowTaskStatus = ShowTask['status'];
/** Read the tasks for a show (viewer+ on the show's band). */
export async function apiGetShowTasks(showId: string): Promise<ShowTask[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/tasks`);
}
/** Create a task on a show (editor+ on the show's band). */
export async function apiCreateShowTask(showId: string, data: Partial<ShowTask>): Promise<ShowTask> {
  return fetchJSON(`${API_BASE}/shows/${showId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
/** Update a task on a show (editor+ on the show's band). */
export async function apiUpdateShowTask(showId: string, taskId: string | number, data: Partial<ShowTask>): Promise<ShowTask> {
  return fetchJSON(`${API_BASE}/shows/${showId}/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
/** Delete a task on a show (editor+ on the show's band). */
export async function apiDeleteShowTask(showId: string, taskId: string | number): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/shows/${showId}/tasks/${taskId}`, {
    method: 'DELETE',
  });
}

/** A file exposed on the public Promo kit share page. */
export interface PromoKitPublicFile {
  id: number;
  filename: string;
  url: string;
  size: number;
  mime: string;
  uploaded_at: string;
}
/**
 * Payload of GET /api/share/promo/:token (public, no login).
 * A password-protected link that has not been unlocked answers
 * `401 { passwordRequired: true, band: { name } }` — the response carries NO
 * content at all, hence the optional fields here. (2026-10-16)
 */
export interface PromoKitPublicDTO {
  token: string;
  band: { id?: string | null; name: string; slug?: string; image_url?: string };
  files: PromoKitPublicFile[];
  /** Structured page content (option C 2026-10-19) when the band/tour authored
   *  one; null/absent = the classic files-only page. */
  content?: PromoContentDTO | null;
  /** Set on the 401 lock answer (see above). */
  passwordRequired?: boolean;
}
/** Fetch the public Promo kit for a band token (no credentials). */
export async function apiGetPromoKit(token: string): Promise<PromoKitPublicDTO> {
  return fetchJSON(`${API_BASE}/share/promo/${encodeURIComponent(token)}`);
}
/**
 * Unlock a password-protected Promo kit link. Sets the signed unlock cookie
 * (7 days, like the rider share), so the next GET returns the content without
 * asking again. (2026-10-16)
 */
export async function apiUnlockPromoKit(token: string, password: string): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/share/promo/${encodeURIComponent(token)}/unlock`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

// ---- Band backup / restore (Band Settings) ----
// Backups are now ENCRYPTED (.bandz.enc, AES-256-CBC) so there is no
// client-side BandBackupDTO to parse — the whole file is opaque bytes.

/** Download the band's full backup as an ENCRYPTED Blob (.bandz.enc). Raw
 * fetch so it is never cached in the offline store and arrives as a Blob. */
export async function apiGetBandBackup(bandId: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}/bands/${encodeURIComponent(bandId)}/backup`, { credentials: 'include' });
  if (!res.ok) {
    let msg = `Backup failed (${res.status})`;
    try { const e = await res.json(); if (e?.error) msg = e.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.blob();
}

/** Restore a band from an ENCRYPTED .bandz.enc file (destructive — replaces
 * the band's data). The raw file bytes are sent as application/octet-stream;
 * the server decrypts with the master key, validates and restores. Requires
 * editor+ on the band. */
export async function apiRestoreBand(bandId: string, encBytes: ArrayBuffer | Blob): Promise<{ restored: boolean }> {
  const res = await fetch(`${API_BASE}/bands/${encodeURIComponent(bandId)}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream' },
    credentials: 'include',
    body: encBytes,
  });
  if (!res.ok) {
    let msg = `Restore failed (${res.status})`;
    try { const e = await res.json(); if (e?.error) msg = e.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

// ---- Venue files (shared catalog — tech sheets / plans per venue) ----
export interface VenueFileDTO {
  id: number;
  venue_id: string;
  filename: string;
  url: string;
  size: number;
  mime: string;
  uploaded_at: string;
  /** 'photo' = l'image imprimée en pleine largeur dans la day sheet. */
  category?: string;
  /** Légende libre imprimée sous la photo (vide ⇒ nom de la salle). */
  caption?: string;
}
export async function apiGetVenueFiles(venueId: string): Promise<VenueFileDTO[]> {
  return fetchJSON(`${API_BASE}/venues/${encodeURIComponent(venueId)}/files`);
}
export async function apiUploadVenueFile(venueId: string, data: { filename: string; mime: string; base64: string }): Promise<VenueFileDTO> {
  return fetchJSON(`${API_BASE}/venues/${encodeURIComponent(venueId)}/files`, { method: 'POST', body: JSON.stringify(data) });
}
/** Légende libre imprimée sous la photo de la salle (vide ⇒ nom de la salle). */
export async function apiSetVenueFileCaption(venueId: string, fileId: number, caption: string): Promise<{ id: number; caption: string }> {
  return fetchJSON(`${API_BASE}/venues/${venueId}/files/${fileId}/caption`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ caption }),
  });
}

/** Désigne LA photo « document » du venue (day sheet, section venue-photo). */
export async function apiSetVenuePhoto(venueId: string, fileId: number, on = true): Promise<{ id?: number; category?: string; cleared?: number }> {
  return fetchJSON(`${API_BASE}/venues/${venueId}/files/${fileId}/photo`, { method: on ? 'POST' : 'DELETE' });
}

export async function apiDeleteVenueFile(venueId: string, id: number): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/venues/${encodeURIComponent(venueId)}/files/${id}`, { method: 'DELETE' });
}

// ---- Global settings (editable app-wide suggestion lists) ----
export async function apiGetSuggestionList(list: string): Promise<string[]> {
  const d = await fetchJSON(`${API_BASE}/settings/${list}`);
  return d?.types || [];
}
export async function apiPutSuggestionList(list: string, types: string[]): Promise<string[]> {
  const d = await fetchJSON(`${API_BASE}/settings/${list}`, { method: 'PUT', body: JSON.stringify({ types }) });
  return d?.types || types;
}
// Convenience wrappers — REMOVED (audit 2026-10-24 [DEAD]): apiGet/PutBacklineTypes
// and apiGet/PutDietTypes had no caller anywhere (the suggestion lists are edited
// directly on their config pages); the generic apiGet/PutSuggestionList stay.

// ---- API keys (admin, for external AI agents) ----
export interface ApiKeyDTO {
  id: number;
  label: string;
  band_id: string | null;
  band_name?: string | null;
  created_by: number;
  last_used_at?: string | null;
  created_at: string;
  revoked: number;
}
export async function apiGetApiKeys(): Promise<ApiKeyDTO[]> {
  return fetchJSON(`${API_BASE}/keys`);
}
export async function apiCreateApiKey(data: { label: string; band_id?: string }): Promise<{ id: number; label: string; band_id: string | null; key: string }> {
  return fetchJSON(`${API_BASE}/keys`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiRevokeApiKey(id: number): Promise<{ revoked: boolean }> {
  return fetchJSON(`${API_BASE}/keys/${id}/revoke`, { method: 'POST' });
}
export async function apiDeleteApiKey(id: number): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/keys/${id}`, { method: 'DELETE' });
}
export async function apiGetApiKeyBands(): Promise<Array<{ id: string; name: string }>> {
  return fetchJSON(`${API_BASE}/keys/meta/bands`);
}

export async function apiCreateBand(data: Partial<BandDTO>): Promise<BandDTO> {
  return fetchJSON(`${API_BASE}/bands`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function apiUpdateBand(id: string, data: Partial<BandDTO>): Promise<BandDTO> {
  return fetchJSON(`${API_BASE}/bands/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/** Geocode the band's Home base address (address/postal_code/city/country) and
    persist the resulting lat/lng server-side. Returns the found coordinates +
    a human label. A band with no address (or an unresolved query) answers 422 —
    the caller should surface the message and keep the previous coordinates. */
export async function apiGeocodeBandAddress(id: string): Promise<{ lat: number; lng: number; label: string }> {
  return fetchJSON(`${API_BASE}/bands/${id}/geocode-address`, { method: 'POST' });
}

export async function apiDeleteBand(id: string): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${id}`, { method: 'DELETE' });
}

/* ----------------------------- Rider (config + musicians + stageplots) ----------------------------- */

export interface RiderDTO {
  config?: any;
  musicians?: any[];
  stageplots?: { version?: number; items?: any[] };
}

export async function apiGetRider(bandId: string): Promise<any | null> {
  try {
    return await fetchJSON(`${API_BASE}/bands/${bandId}/rider`);
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function apiSaveRider(bandId: string, rider: RiderDTO): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/rider`, {
    method: 'PUT',
    body: JSON.stringify(rider),
  });
}

/**
 * Optimistic-concurrency token carried by every layout read/write (2026-09-16).
 * `lastModified` is the `pdf_layouts.last_modified` the editor loaded; echo it
 * back on save and the server refuses (409) a write made stale by another
 * tab/session instead of silently clobbering the fresher layout.
 */
export interface LayoutMeta<T = any> {
  data: T | null;
  lastModified: number | null;
  globalLastModified?: number | null;
}

/** True when a layout write was refused because someone else wrote it first. */
export function isLayoutConflict(err: unknown): boolean {
  const e = err as any;
  if (!e) return false;
  if (e.status === 409) return true;
  return e?.body?.code === 'layout_conflict';
}

/* ----------------------------- PDF layout (rider editor) ----------------------------- */
// Persistence for the admin "Rider Editor" page: a global template
// (scope_key 'global') plus a per-band override. The frontend owns the
// exact layout shape (see src/lib/riderLayout.ts). 404 (no row yet) is
// a normal state — callers fall back to createDefaultPdfLayout().

export async function apiGetPdfLayoutGlobal(): Promise<any | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/pdf-layouts/global`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

export async function apiSavePdfLayoutGlobal(layout: any, lastModified?: number | null): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/pdf-layouts/global`, {
    method: 'PUT',
    // lastModified = the token this editor LOADED: the server answers 409
    // instead of overwriting when another tab/session wrote the layout first.
    body: JSON.stringify({ data: layout, lastModified: lastModified ?? null }),
  });
}

export async function apiGetPdfLayoutBand(bandId: string): Promise<any | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/pdf-layout`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

export async function apiSavePdfLayoutBand(bandId: string, layout: any, lastModified?: number | null): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/pdf-layout`, {
    method: 'PUT',
    // lastModified = the token this editor LOADED: the server answers 409
    // instead of overwriting when another tab/session wrote the layout first.
    body: JSON.stringify({ data: layout, lastModified: lastModified ?? null }),
  });
}

export async function apiGetPdfLayoutEffective(bandId: string): Promise<any | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/pdf-layout/effective`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

/* ----------------------------- Daysheet layout (daysheet editor) ----------------------------- */
// Persistence for the admin "Daysheet Layout" page: a global template plus a
// per-band override, in the SAME `pdf_layouts` table under a distinct
// 'daysheet:' scope prefix. Shape lives in src/lib/daysheetLayout.ts. 404 (no
// row yet) is a normal state — callers fall back to createDefaultDaysheetLayout().

export type DaysheetLayoutDTO = DaysheetLayout;

export async function apiGetDaysheetLayoutGlobal(): Promise<DaysheetLayoutDTO | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/daysheet-layouts/global`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

export async function apiSaveDaysheetLayoutGlobal(layout: DaysheetLayoutDTO, lastModified?: number | null): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/daysheet-layouts/global`, {
    method: 'PUT',
    // lastModified = the token this editor LOADED: the server answers 409
    // instead of overwriting when another tab/session wrote the layout first.
    body: JSON.stringify({ data: layout, lastModified: lastModified ?? null }),
  });
}

export async function apiGetDaysheetLayoutBand(bandId: string): Promise<DaysheetLayoutDTO | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/daysheet-layout`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

export async function apiSaveDaysheetLayoutBand(bandId: string, layout: DaysheetLayoutDTO, lastModified?: number | null): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/daysheet-layout`, {
    method: 'PUT',
    // lastModified = the token this editor LOADED: the server answers 409
    // instead of overwriting when another tab/session wrote the layout first.
    body: JSON.stringify({ data: layout, lastModified: lastModified ?? null }),
  });
}

export async function apiGetDaysheetLayoutEffective(bandId: string): Promise<DaysheetLayoutDTO | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/daysheet-layout/effective`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

/* ----------------------------- Tour Book layout (tour book editor) ----------------------------- */
// Persistence for the admin "Tour Book Layout" page: a global template plus a
// per-band override, in the SAME `pdf_layouts` table under a distinct
// 'tourbook:' scope prefix. Shape lives in src/lib/tourbookLayout.ts. 404 (no
// row yet) is a normal state — callers fall back to createDefaultTourBookLayout().

export type TourBookLayoutDTO = TourBookLayout;

export async function apiGetTourBookLayoutGlobal(): Promise<TourBookLayoutDTO | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/tourbook-layouts/global`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

export async function apiSaveTourBookLayoutGlobal(layout: TourBookLayoutDTO, lastModified?: number | null): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/tourbook-layouts/global`, {
    method: 'PUT',
    // lastModified = the token this editor LOADED: the server answers 409
    // instead of overwriting when another tab/session wrote the layout first.
    body: JSON.stringify({ data: layout, lastModified: lastModified ?? null }),
  });
}

export async function apiGetTourBookLayoutBand(bandId: string): Promise<TourBookLayoutDTO | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/tourbook-layout`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

export async function apiSaveTourBookLayoutBand(bandId: string, layout: TourBookLayoutDTO, lastModified?: number | null): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/tourbook-layout`, {
    method: 'PUT',
    // lastModified = the token this editor LOADED: the server answers 409
    // instead of overwriting when another tab/session wrote the layout first.
    body: JSON.stringify({ data: layout, lastModified: lastModified ?? null }),
  });
}

export async function apiGetTourBookLayoutEffective(bandId: string): Promise<TourBookLayoutDTO | null> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/tourbook-layout/effective`);
    return (res as any)?.data ?? null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

/* ---- concurrency-aware reads (data + lastModified) ---- */
// Same endpoints as the plain getters, but they ALSO expose the server's
// `last_modified`, which the editors echo back on save.

export async function apiGetPdfLayoutGlobalMeta(): Promise<LayoutMeta> {
  try {
    const res = await fetchJSON(`${API_BASE}/pdf-layouts/global`);
    return {
      data: (res as any)?.data ?? null,
      lastModified: (res as any)?.lastModified ?? null,
    };
  } catch (err: any) {
    if (err?.status === 404) return { data: null, lastModified: null };
    throw err;
  }
}

export async function apiGetPdfLayoutBandMeta(bandId: string): Promise<LayoutMeta> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/pdf-layout`);
    return {
      data: (res as any)?.data ?? null,
      lastModified: (res as any)?.lastModified ?? null,
    };
  } catch (err: any) {
    if (err?.status === 404) return { data: null, lastModified: null };
    throw err;
  }
}

export async function apiGetPdfLayoutEffectiveMeta(bandId: string): Promise<LayoutMeta> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/pdf-layout/effective`);
    return {
      data: (res as any)?.data ?? null,
      lastModified: (res as any)?.lastModified ?? null,
      globalLastModified: (res as any)?.globalLastModified ?? null,
    };
  } catch (err: any) {
    if (err?.status === 404) return { data: null, lastModified: null };
    throw err;
  }
}

export async function apiGetDaysheetLayoutGlobalMeta(): Promise<LayoutMeta> {
  try {
    const res = await fetchJSON(`${API_BASE}/daysheet-layouts/global`);
    return {
      data: (res as any)?.data ?? null,
      lastModified: (res as any)?.lastModified ?? null,
    };
  } catch (err: any) {
    if (err?.status === 404) return { data: null, lastModified: null };
    throw err;
  }
}

export async function apiGetDaysheetLayoutEffectiveMeta(bandId: string): Promise<LayoutMeta> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/daysheet-layout/effective`);
    return {
      data: (res as any)?.data ?? null,
      lastModified: (res as any)?.lastModified ?? null,
      globalLastModified: (res as any)?.globalLastModified ?? null,
    };
  } catch (err: any) {
    if (err?.status === 404) return { data: null, lastModified: null };
    throw err;
  }
}

export async function apiGetTourBookLayoutGlobalMeta(): Promise<LayoutMeta> {
  try {
    const res = await fetchJSON(`${API_BASE}/tourbook-layouts/global`);
    return {
      data: (res as any)?.data ?? null,
      lastModified: (res as any)?.lastModified ?? null,
    };
  } catch (err: any) {
    if (err?.status === 404) return { data: null, lastModified: null };
    throw err;
  }
}

export async function apiGetTourBookLayoutEffectiveMeta(bandId: string): Promise<LayoutMeta> {
  try {
    const res = await fetchJSON(`${API_BASE}/bands/${bandId}/tourbook-layout/effective`);
    return {
      data: (res as any)?.data ?? null,
      lastModified: (res as any)?.lastModified ?? null,
      globalLastModified: (res as any)?.globalLastModified ?? null,
    };
  } catch (err: any) {
    if (err?.status === 404) return { data: null, lastModified: null };
    throw err;
  }
}

/* ----------------------------- Layout presets ----------------------------- */
// Named layout presets (rider / daysheet / tourbook) reusable across the
// global and per-band scopes. Stored server-side in the `layout_presets` table.
// Global presets: admin writes, any authenticated read. Band presets: owner/
// editor writes, band members read. See server/routes/layout-presets.js.

export type LayoutPresetKind = 'rider' | 'daysheet' | 'tourbook';
export type LayoutPresetScope = 'global' | 'band';

export interface LayoutPresetSummary {
  id: number;
  name: string;
  kind: LayoutPresetKind;
  scope: LayoutPresetScope;
  band_id: string | null;
  /** Band display name (badge), joined server-side; null for global presets. */
  band_name?: string | null;
  /** Server-computed write right on THIS preset (global=admin, band=owner/editor). */
  can_write?: boolean;
  updated_at: number;
}

export interface LayoutPresetDetail extends LayoutPresetSummary {
  data: any;
  created_at: number;
}

/** GET /api/layout-presets → every preset the user can reach (unified scope). */
export interface LayoutPresetList {
  presets: LayoutPresetSummary[];
  /** May the user write the global template (platform admin)? */
  canWriteGlobal: boolean;
  /** Band ids the user may write (owner/editor). */
  canWriteBands: string[];
}

export async function apiListLayoutPresets(kind: LayoutPresetKind): Promise<LayoutPresetList> {
  const qs = new URLSearchParams({ kind });
  const res = (await fetchJSON(`${API_BASE}/layout-presets?${qs.toString()}`)) as any;
  return {
    presets: res?.presets ?? [],
    canWriteGlobal: !!res?.can_write_global,
    canWriteBands: Array.isArray(res?.can_write_bands) ? res.can_write_bands : [],
  };
}

export async function apiGetLayoutPreset(id: number): Promise<LayoutPresetDetail> {
  return fetchJSON(`${API_BASE}/layout-presets/${id}`);
}

export async function apiCreateLayoutPreset(input: {
  kind: LayoutPresetKind;
  scope: LayoutPresetScope;
  bandId?: string | null;
  name: string;
  data: any;
}): Promise<{ id: number }> {
  return fetchJSON(`${API_BASE}/layout-presets`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function apiUpdateLayoutPreset(
  id: number,
  input: { name?: string; data?: any },
): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/layout-presets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function apiDeleteLayoutPreset(id: number): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/layout-presets/${id}`, { method: 'DELETE' });
}

export async function apiApplyLayoutPreset(
  id: number,
  scope: LayoutPresetScope,
  bandId?: string | null,
): Promise<{ success: boolean; scopeKey: string }> {
  return fetchJSON(`${API_BASE}/layout-presets/${id}/apply`, {
    method: 'POST',
    body: JSON.stringify({ scope, bandId }),
  });
}

/* Granular rider endpoints (optional) */

/** A non-blocking rack-occupation conflict returned by the server on PUT of
 * rider_config (mode AVERTIR — the save always succeeds; this is advisory). */
export interface RackConflictWarning {
  type: 'rack-conflict';
  kind: 'inputs' | 'outputs';
  rackId: string;
  rackName: string;
  channel: number;
  occupantA: string;
  occupantB: string;
  message: string;
}

export async function apiGetConfig(bandId: string): Promise<any | null> {
  try {
    return await fetchJSON(`${API_BASE}/bands/${bandId}/rider/config`);
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Non-blocking warning emitted when the server KEPT a stored value the client
 *  did not declare as edited (anti data-loss merge). */
export interface ConfigPreservedWarning {
  type: 'preserved';
  section: string;
  message: string;
}
export type ConfigSaveWarning = RackConflictWarning | ConfigPreservedWarning;

export interface ConfigSaveOptions {
  /** The sections the client REALLY edited, as `section` or `section.transport`
   *  entries (e.g. `['stageDesign.van']`). Absent/empty => legacy full replace. */
  touched?: string[];
  /** `rider_config.last_modified` the client loaded. Sent as `X-Config-Version`
   *  for the server's concurrency guard (a strictly newer stored row => 409
   *  `stale`). Omitted while offline (the queue drops headers) so a replayed
   *  save is never wedged by a stale-version 409. */
  version?: number | null;
}

export async function apiSaveConfig(
  bandId: string,
  config: any,
  opts?: ConfigSaveOptions,
): Promise<{ success: boolean; warnings?: ConfigSaveWarning[]; version?: number | null }> {
  const headers: Record<string, string> = {};
  if (opts && typeof opts.version === 'number' && Number.isFinite(opts.version)) {
    headers['X-Config-Version'] = String(opts.version);
  }
  // Only declare `__touched` when the client actually tracked edits — otherwise
  // fall back to the historical full-replace payload (retro-compatible).
  const body =
    opts && Array.isArray(opts.touched) && opts.touched.length > 0
      ? { ...config, __touched: opts.touched }
      : config;
  const res = await fetchJSON(`${API_BASE}/bands/${bandId}/rider/config`, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers,
  });
  // New response shape: { ok, data, warnings } (rack-usage + preservation
  // warnings, mode AVERTIR). Older servers return the raw config blob.
  if (res && typeof res === 'object' && 'warnings' in res) {
    const data = (res as { data?: { __lastModified?: number } }).data;
    return {
      success: true,
      warnings: Array.isArray(res.warnings) ? res.warnings : [],
      version: data && typeof data.__lastModified === 'number' ? data.__lastModified : undefined,
    };
  }
  return { success: true };
}

export async function apiGetMusicians(bandId: string): Promise<any[] | null> {
  try {
    return await fetchJSON(`${API_BASE}/bands/${bandId}/rider/musicians`);
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** The rider-list version handshake result: the list plus the
 *  `rider_musicians.last_modified` / `rider_crew.last_modified` (epoch ms) the
 *  server reported in `X-Rider-Version`. `version` is null when the server sent
 *  no header (legacy backend) or the reply came from the offline cache. */
export interface RiderListMeta {
  data: any[] | null;
  version: number | null;
}

/** GET musicians WITH the concurrency version (header `X-Rider-Version`). */
export async function apiGetMusiciansMeta(bandId: string): Promise<RiderListMeta> {
  try {
    const { data, headers } = await fetchJSONMeta(`${API_BASE}/bands/${bandId}/rider/musicians`);
    return { data: data ?? null, version: riderVersionFromHeaders(headers) };
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return { data: null, version: null };
    throw err;
  }
}

/** PUT musicians, echoing the loaded version in `X-Rider-Version` so the server
 *  can refuse a stale write (409 `stale`, nothing written). Returns the new
 *  version to adopt. */
/**
 * « Sa fiche » TECHNIQUE — the user's OWN musician/crew card (instrument, backline,
 * IEM). Self-service: membership of the band is the only condition, no capability
 * needed. The server edits ONLY the whitelisted technical fields of the caller's
 * entry (`/api/me/rider-card`, mounted before the global read-only guard).
 */
export interface MyRiderCardDTO {
  kind: 'musicians' | 'crew';
  band_id: string;
  card_id: string;
  name: string;
  fields: Record<string, unknown>;
}

/** The caller's own rider card for a band, or null when he has none there. */
export async function apiGetMyRiderCard(bandId: string): Promise<MyRiderCardDTO | null> {
  try {
    return await fetchJSON(`${API_BASE}/me/rider-card/${bandId}`);
  } catch (err: unknown) {
    // A member without a card in that band is a NORMAL case (404 by design).
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Save the TECHNICAL fields of the caller's own card (whitelist enforced server-side). */
export async function apiSaveMyRiderCard(
  bandId: string,
  fields: Record<string, unknown>,
): Promise<MyRiderCardDTO> {
  return fetchJSON(`${API_BASE}/me/rider-card/${bandId}`, {
    method: 'PUT',
    body: JSON.stringify(fields),
  });
}

export async function apiSaveMusicians(
  bandId: string,
  musicians: any[],
  opts?: { version?: number | null },
): Promise<{ success: boolean; version: number | null }> {
  const headers: Record<string, string> = {};
  if (opts && typeof opts.version === 'number' && Number.isFinite(opts.version)) {
    headers['X-Rider-Version'] = String(opts.version);
  }
  const { headers: resHeaders } = await fetchJSONMeta(`${API_BASE}/bands/${bandId}/rider/musicians`, {
    method: 'PUT',
    body: JSON.stringify(musicians),
    headers,
  });
  return { success: true, version: riderVersionFromHeaders(resHeaders) };
}

export async function apiGetCrew(bandId: string): Promise<any[] | null> {
  try {
    return await fetchJSON(`${API_BASE}/bands/${bandId}/rider/crew`);
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}
/** GET crew WITH the concurrency version (header `X-Rider-Version`). */
export async function apiGetCrewMeta(bandId: string): Promise<RiderListMeta> {
  try {
    const { data, headers } = await fetchJSONMeta(`${API_BASE}/bands/${bandId}/rider/crew`);
    return { data: data ?? null, version: riderVersionFromHeaders(headers) };
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return { data: null, version: null };
    throw err;
  }
}
/** PUT crew, echoing the loaded version in `X-Rider-Version` (409 `stale` guard). */
export async function apiSaveCrew(
  bandId: string,
  crew: any[],
  opts?: { version?: number | null },
): Promise<{ success: boolean; version: number | null }> {
  const headers: Record<string, string> = {};
  if (opts && typeof opts.version === 'number' && Number.isFinite(opts.version)) {
    headers['X-Rider-Version'] = String(opts.version);
  }
  const { headers: resHeaders } = await fetchJSONMeta(`${API_BASE}/bands/${bandId}/rider/crew`, {
    method: 'PUT',
    body: JSON.stringify(crew),
    headers,
  });
  return { success: true, version: riderVersionFromHeaders(resHeaders) };
}

export async function apiGetStagePlots(bandId: string): Promise<any | null> {
  // BUG-1 (2026-08-28): the legacy endpoint /api/bands/:id/rider/stageplots reads
  // the legacy rider_stageplots table (empty for My Band/SAMAEL → 404). Point to
  // the ACTIVE /api/stageplots endpoint (table stage_plots) instead. Used only by
  // auto-preload/offline cache, so the response shape is not read by callers.
  try {
    return await fetchJSON(`${API_BASE}/stageplots?bandId=${encodeURIComponent(bandId)}`);
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// apiSaveStagePlots REMOVED (audit 2026-10-24 [DEAD]): PUT /bands/:id/rider/stageplots
// has no server route AND the function had no caller.

/* ---- AI copilot (rider import) ---- */

/** One transport segment proposed by the read-only `import_flights` AI task.
 *
 * Every value is a VERBATIM copy from the booking document: the server
 * normalizes the shape tolerant-wise but validates by code (ISO date, HH:MM
 * time, IATA against the catalog) and drops what it cannot vouch for — it never
 * guesses. Empty strings mean "not in the document". */
export interface AiProposedLeg {
  mode: 'flight' | 'train' | 'ferry';
  flight_date: string;
  airline: string;
  flight_no: string;
  booking_ref: string;
  departure: string;
  arrival: string;
  from_city: string;
  from_iata: string;
  from_station: string;
  to_city: string;
  to_iata: string;
  to_station: string;
  train_class: string;
  coach: string;
  seat: string;
  platform: string;
  passenger_names: string[];
  /** The model's own 0..1 self-assessment (informational only). */
  confidence: number | null;
}

export interface AiAssistResult {
  output: string;
  proposedFields: Record<string, any>;
  /** Present for the `import_flights` task: the extracted transport segments. */
  proposedLegs?: AiProposedLeg[];
  /** Present for the `import_rider_personnel` task: the extracted personnel. */
  proposedPersonnel?: {
    musicians: { name: string; instruments: { name: string; channels?: number }[] }[];
    crew: { name: string; role: string }[];
  };
  /** Present for PDF imports when a drawn stage plot was detected and
   * rasterized into small PNG data URLs. Additive — old callers ignore it. */
  stagePlotImageCandidates?: { page: number; mime: string; dataUrl: string }[];
  /** Non-fatal warnings (e.g. a rasterization was skipped). Never blocks the import. */
    warnings?: string[];
    /** True when the PDF had no text layer and the text was recovered by OCR. */
    ocrUsed?: boolean;
    /** The OCR language pack actually used (e.g. 'fra+eng'). */
    ocrLangs?: string | null;
    /** Number of pages OCR-processed. */
        ocrPages?: number;
        provider: string;
      usage: { tokensIn: number; tokensOut: number };
  /** Credits left after this call (present on the metered tasks). */
  remaining?: number;
  unlimited?: boolean;
}

export interface AiApplyResult {
  bandId: string;
  sectionsMerged: string[];
  config: any;
}

/** Response of `POST /api/ai/assist` with `task: 'import_flights'` (read-only:
 * extracting segments never writes a row; the confirmed apply does). */
export interface AiImportFlightsResponse extends AiAssistResult {
  proposedLegs: AiProposedLeg[];
  warnings: string[];
  remaining: number;
  unlimited?: boolean;
}

/** A show field PROPOSED by the read-only `import_show` AI task. The server
 * validates every value by code (real ISO date, HH:MM, real `tour_dates`
 * enums, 3-letter currency, plain amounts) and drops what it cannot vouch for
 * — a rejected value lands in `unreadable`, never here. */
export type AiProposedShowFields = Record<string, string | number>;

/** An existing venue the server matched against the document (name + city).
 * The AI task NEVER creates a venue. */
export interface AiVenueCandidate {
  id: string | number;
  name: string;
  city: string;
  country: string;
}

/** Response of `POST /api/ai/assist` with `task: 'import_show'` (read-only:
 * proposing fields never writes a row; the confirmed apply does). */
export interface AiImportShowResponse extends AiAssistResult {
  proposedFields: AiProposedShowFields;
  show: AiProposedShowFields;
  venueQuery: string;
  venueCountry?: string;
  venueCandidates: AiVenueCandidate[];
  confidence: number | null;
  /** Field names / fragments the model could not read — shown in plain text. */
  unreadable: string[];
  warnings: string[];
  /** True when the image actually left the server to the AI provider. */
  imageSent: boolean;
  remaining: number;
  unlimited?: boolean;
}

/** Ask the AI to run a metered, read-only task. `import_rider` maps raw rider
 * text / an uploaded PDF to rider_config sections (nothing is persisted until
 * /api/ai/apply is called). `check_itinerary` and `prepare_setlist` are pure
 * proposals — nothing is ever written server-side. `import_flights` extracts
 * transport segments from a booking confirmation (text or PDF). `import_show`
 * proposes the fields of ONE show from text, a PDF (OCR included) or an image
 * (vision — the caller must have collected the user's consent). */
export async function apiAiAssist(body: {
  task: 'import_show';
  bandId: string;
  text?: string;
  pdfBase64?: string;
  /** Vision input — `import_show` only, ≤ 4 MB, PNG/JPEG/WebP/GIF. */
  imageBase64?: string;
  imageMime?: string;
}): Promise<AiImportShowResponse>;
export async function apiAiAssist(body: {
  task: 'import_flights';
  bandId: string;
  text?: string;
  pdfBase64?: string;
  showId?: string | number;
}): Promise<AiImportFlightsResponse>;
export async function apiAiAssist(body: {
  task: 'import_rider' | 'import_rider_personnel' | 'check_itinerary' | 'prepare_setlist';
  bandId: string;
  text?: string;
  pdfBase64?: string;
  /** Optional target show (prepare_setlist adapts the proposal to its duration). */
  showId?: string | number;
}): Promise<AiAssistResult>;
export async function apiAiAssist(body: {
  task: 'import_rider' | 'import_rider_personnel' | 'check_itinerary' | 'prepare_setlist' | 'import_flights' | 'import_show';
  bandId: string;
  text?: string;
  pdfBase64?: string;
  imageBase64?: string;
  imageMime?: string;
  showId?: string | number;
}): Promise<AiAssistResult | AiImportFlightsResponse | AiImportShowResponse> {
  return fetchJSON(`${API_BASE}/ai/assist`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Persist a user-validated AI mapping into the band's rider_config. */
export async function apiAiApply(body: {
  bandId: string;
  sections: Record<string, any>;
}): Promise<AiApplyResult> {
  return fetchJSON(`${API_BASE}/ai/apply`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** One proposed instrument/role -> palette-icon mapping from the AI. */
export interface StageItemMapping {
  name: string;
  iconId: string | null;
  category: string | null;
  confidence: number | null;
}

export interface AiMapStageItemsResult {
  output: string;
  mappings: StageItemMapping[];
  /** True when the item list was truncated to 60 before the call. */
  truncated: boolean;
  provider: string;
  usage: { tokensIn: number; tokensOut: number; credits: number };
  remaining: number;
  unlimited?: boolean;
}

/** Ask the AI to map free-text instrument/role names to the CLOSED stage-plot
 * palette. Read-only + metered (402 when out of credits). The server enforces
 * the allowlist: any iconId outside the palette is rejected (null). */
export async function apiAiMapStageItems(body: {
  bandId: string;
  items: { name: string; kind: 'instrument' | 'role' }[];
}): Promise<AiMapStageItemsResult> {
  return fetchJSON(`${API_BASE}/ai/assist`, {
    method: 'POST',
    body: JSON.stringify({ task: 'map_stage_items', ...body }),
  });
}

/** A single structured action proposed by the stage-plot placement assistant. */
export type StageCommandAction =
  | { op: 'add'; iconId: string; x: number; y: number; label?: string }
  | { op: 'move'; id: string; x: number; y: number }
  | { op: 'remove'; id: string }
  | { op: 'rotate'; id: string; rotation: number }
  | { op: 'arrange'; preset: 'standard' | 'backline' | 'monitors' };

export interface StageCommandResult {
  reply: string;
  actions: StageCommandAction[];
  rejected: unknown[];
  provider: string;
  usage: { tokensIn: number; tokensOut: number; credits: number };
  remaining: number;
  unlimited?: boolean;
}

/** Ask the AI to propose placement actions for a natural-language stage-plot
 * command. READ-ONLY + metered (402 when out of credits): it only PROPOSES —
 * the client applies the actions to its local store and the normal editor Save
 * persists the plot. The server validates every action against a strict
 * allowlist and drops anything invalid. */
export async function apiAiStageplotCommand(body: {
  bandId: string;
  message: string;
  stage: { width: number; height: number; unit: string };
  elements: { id: string; label: string; iconId: string; x: number; y: number; rotation: number }[];
  iconIds: string[];
}): Promise<StageCommandResult> {
  return fetchJSON(`${API_BASE}/ai/stageplot-command`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface AiCreditsInfo {
  /** Total credits available (allowance + top-up). Back-compat alias for `total`. */
  credits: number;
  /** Monthly subscription allowance (resets each month to plan_allowance). */
  allowance: number;
  /** Persistent top-up credits (never reset, accumulate). */
  topup: number;
  /** Total available across both pools (allowance + topup). */
  total: number;
  /** The user's plan: 'trial' | 'solo' | 'pro' | 'unlimited'. 'trial' = the 14-day
   self-service trial: its allowance is 0 (the trial grants a fixed total instead). */
  plan: string;
  /** The monthly allowance granted by the plan (Solo 5 / Pro 25 / Unlimited 50). */
  plan_allowance: number;
  /** Current billing cycle 'YYYY-MM' (when the allowance was last reset). */
  plan_cycle: string;
  /** Total credits debited so far (SUM of ai_usage_log.credits_debited). */
  consumed: number;
  /** True for platform admins: never blocked and never debited. */
  unlimited: boolean;
  updatedAt: string | null;
  ratio: { tokensPerCredit: number; maxCreditsPerTask: number };
}

/** The signed-in user's current AI credit balance (metering, I2). */
export async function apiAiCredits(): Promise<AiCreditsInfo> {
  return fetchJSON(`${API_BASE}/ai/credits`);
}

/* ---- TM / MT one-click import (Unlimited plan) ---- */
// Key management is user-scoped; the TM API key is encrypted at rest server-side
// and NEVER returned to the client — the status endpoint only reports hasKey.

export interface ImportCounts {
  created: number;
  skipped: number;
  filtered?: number;
  total: number;
}

export interface TmImportResult {
  ok: boolean;
  bandId: string;
  tmBands: string[];
  events: number;
  shows: ImportCounts;
  timezones: { matched: number; empty: number; skipped: number; updated: number; noMatch: number };
  hotels: { created: number; filled: number; exists: number; noShow: number; total: number };
  contacts: { created: number; linked: number; skipped: number; total: number };
}

export interface MtImportResult {
  ok: boolean;
  bandId: string;
  rows: number;
  columns: string[];
  shows: ImportCounts;
}

export interface TmStatusResult {
  ok: boolean;
  hasKey: boolean;
}

/** Is a TourManager API key saved for this account? (never exposes the key itself) */
export async function apiGetImportStatus(): Promise<TmStatusResult> {
  return fetchJSON(`${API_BASE}/import/tm-status`);
}

/** Save the user's own TourManager API key (encrypted at rest). */
export async function apiSaveTmKey(key: string): Promise<{ ok: boolean; stored: boolean }> {
  return fetchJSON(`${API_BASE}/import/tm-key`, { method: 'PUT', body: JSON.stringify({ key }) });
}

/** Forget the saved TourManager API key. */
export async function apiDeleteTmKey(): Promise<{ ok: boolean; deleted: boolean }> {
  return fetchJSON(`${API_BASE}/import/tm-key`, { method: 'DELETE' });
}

/** One-click TourManager import (shows + timezones + hotels + contacts) into a band. */
export async function apiRunTmImport(bandId: string): Promise<TmImportResult> {
  return fetchJSON(`${API_BASE}/import/tm/run`, { method: 'POST', body: JSON.stringify({ bandId }) });
}

/** Import shows from a MasterTour CSV export into a band. */
export async function apiImportMtCsv(bandId: string, csv: string): Promise<MtImportResult> {
  return fetchJSON(`${API_BASE}/import/mt/csv`, { method: 'POST', body: JSON.stringify({ bandId, csv }) });
}

/* ---- Stripe subscriptions (Phase 2 billing) ---- */

export interface BillingTier {
  id: string;
  name: string;
  price: number;
  launch: number;
  allowance: number;
  bands: number | null;
  api_included: boolean;
  mcp_included: boolean;
  price_configured: boolean;
  launch_price_configured: boolean;
}

export interface BillingAddon {
  id: string;
  name: string;
  price: number;
  configured: boolean;
}

export interface BillingSubscription {
  user_id: string;
  plan: string;
  status: string;
  api_access: boolean;
  mcp_access: boolean;
  launch_price: boolean;
  current_period_end: string;
  updated_at: string;
}

export interface BillingPlansInfo {
  configured: boolean;
  currency: string;
  launch_available: boolean;
  tiers: BillingTier[];
  addons: BillingAddon[];
  subscription: BillingSubscription | null;
}

/** Pricing tiers + add-ons + the caller's current subscription (billing). */
export async function apiGetBillingPlans(): Promise<BillingPlansInfo> {
  return fetchJSON(`${API_BASE}/billing/plans`);
}

/** Create a Stripe Checkout session; returns the hosted redirect URL. */
export async function apiBillingCheckout(body: { plan: string; addons: string[]; launch?: boolean }): Promise<{ url: string }> {
  return fetchJSON(`${API_BASE}/billing/checkout`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Create a Stripe Customer Portal session; returns the hosted redirect URL. */
export async function apiBillingPortal(): Promise<{ url: string }> {
  return fetchJSON(`${API_BASE}/billing/portal`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/* ---- One-time AI credit top-up packs (Stripe mode: payment) ---- */

export interface CreditPack {
  id: string;
  credits: number;
  price: { amount: number; currency: string };
}

export interface CreditPacksInfo {
  configured: boolean;
  currency: string;
  packs: CreditPack[];
}

/** The available one-time AI credit top-up packs (balance + packs + Buy). */
export async function apiBillingCreditPacks(): Promise<CreditPacksInfo> {
  return fetchJSON(`${API_BASE}/billing/credits/packs`);
}

/** Create a Stripe Checkout session for a ONE-TIME credit pack; returns the hosted redirect URL. */
export async function apiBillingCreditCheckout(packId: string): Promise<{ url: string }> {
  return fetchJSON(`${API_BASE}/billing/credits/checkout`, {
    method: 'POST',
    body: JSON.stringify({ packId }),
  });
}

/* ---- AI assistant (read-only chat) ---- */

export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiChatResult {
  reply: string;
  provider: string;
  usage: { tokensIn: number; tokensOut: number; credits: number };
  remaining: number;
  unlimited?: boolean;
}

/** Ask the AI assistant a read-only question about the given band. Metered:
 * debits credits (402 when the balance is exhausted). Nothing is written. */
export async function apiAiChat(body: {
  bandId: string;
  message: string;
}): Promise<AiChatResult> {
  return fetchJSON(`${API_BASE}/ai/chat`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/* ---- AI assistant ACTIONS (propose -> confirm -> apply) ---- */

/** A structured change the AI proposes; the user confirms it before it is
 * applied. `action` is null when the request was a plain question — `summary`
 * then holds the read-only answer. MVP scope is non-destructive only. */
export interface ProposedAction {
  action: 'update_show' | 'add_roadmap_item' | 'add_flight_leg' | 'update_show_fields' | null;
  showId?: string | number | null;
  changes?: Record<string, unknown>;
  /** `update_show_fields` only: explicit confirmation that the money/contractual
   * group may be written. Without it the server ignores every money value. */
  confirmed_money?: boolean;
  /** `add_flight_leg` only: several segments in ONE proposal. Each entry is
   * confirmable on its own (one `POST /ai/chat/apply` per segment). */
  legs?: Array<{ showId: string | number; changes: Record<string, unknown> }>;
  summary: string;
  confidence?: number | null;
}

export interface AiChatActionResult extends ProposedAction {
  provider: string;
  usage: { tokensIn: number; tokensOut: number; credits: number };
  remaining: number;
  unlimited?: boolean;
}

/** Ask the AI to PROPOSE a structured, non-destructive action for a change
 * request. Read-only — nothing is written until /api/ai/chat/apply is called
 * (after the user confirms in the UI). Metered (402 when out of credits). */
export async function apiAiChatAction(body: {
  bandId: string;
  message: string;
}): Promise<AiChatActionResult> {
  return fetchJSON(`${API_BASE}/ai/chat/action`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface AiChatApplyResult {
  applied: boolean;
  action: 'update_show' | 'add_roadmap_item' | 'add_flight_leg' | 'update_show_fields';
  label: string;
  showId: string | number;
  summary: string;
  /** `'duplicate'` when `add_flight_leg` found an equivalent segment on the
   * show: nothing was written and `result.existingId` names the row kept. */
  skipped?: 'duplicate';
  result: Record<string, unknown>;
}

/** Apply a user-confirmed AI action. Server re-validates tenant + canWriteBand
 * and re-checks the show belongs to the band before writing. */
export async function apiAiChatApply(body: {
  bandId: string;
  action: ProposedAction;
}): Promise<AiChatApplyResult> {
  return fetchJSON(`${API_BASE}/ai/chat/apply`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/* ---- Rider share info (shared by the per-show rider sharing API) ---- */

export interface RiderShareInfo {
  enabled: boolean;
  token: string | null;
  hasPassword?: boolean;
}

// ---- Promoter share for a SHOW's rider (central entity since 2026-08-07) ----
/** Current share token for a show's rider (null when not yet shared). */
export async function apiGetRiderShareByRider(riderId: string): Promise<RiderShareInfo> {
  return fetchJSON(`${API_BASE}/riders/${riderId}/share`);
}

/** Create/return the share token for a show's rider (idempotent). */
export async function apiEnableRiderShareByRider(riderId: string, password?: string): Promise<RiderShareInfo> {
  return fetchJSON(`${API_BASE}/riders/${riderId}/share`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

/* ---- Public rider share (no auth) ---- */

export interface RiderShareInputTrack {
  itemKey: string;
  itemLabel: string;
  category: string;
  section?: string;
  instrument?: string;
  mic?: string;
  stand?: string;
  connectionType?: 'wired' | 'wireless';
  providedBy?: string;
  /** Who provides this item: 'band' (the band brings it) or 'venue' (the venue
   * provides it). Sent by the public rider-share endpoint for each item. */
  provider?: string;
  channel?: number;
  detail?: string;
  quantity?: number;
  quantity_changed?: boolean;
  confirmed: number;
  remarks: string;
  updated_at?: string | null;
}

export interface RiderShareItem extends RiderShareInputTrack {
  quantity?: number;
  quantity_changed?: boolean;
}

export interface RiderShareDTO {
  token: string;
  band: { id: string; name: string };
  transport: string;
  sections: Record<string, any>;
  inputList: RiderShareInputTrack[];
  items: RiderShareItem[];
  locked?: boolean;
  hasPassword?: boolean;
  showName?: string | null;
  riderId?: string | null;
}

/** Fetch the public read-only rider + validations for a share token. */
export async function apiGetPublicRiderShare(token: string, transport?: string): Promise<RiderShareDTO> {
  const qs = transport ? `?transport=${encodeURIComponent(transport)}` : '';
  return fetchJSON(`${API_BASE}/share/rider/${token}${qs}`);
}

/** Unlock a password-protected share (public — sets an auth cookie). */
export async function apiUnlockRiderShare(token: string, password: string): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/share/rider/${token}/unlock`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

/** Save a checkbox + remarks for one item (public — no credentials). */
export async function apiValidateRiderShare(
  token: string,
  itemKey: string,
  confirmed: boolean,
  remarks: string,
  transport?: string,
): Promise<{ itemKey: string; itemLabel: string; confirmed: number; remarks: string; updated_at: string }> {
  const qs = transport ? `?transport=${encodeURIComponent(transport)}` : '';
  return fetchJSON(`${API_BASE}/share/rider/${token}/validate${qs}`, {
    method: 'POST',
    body: JSON.stringify({ itemKey, confirmed, remarks }),
  });
}

/* ----------------------------- Advancing (per-show) ---------------------- */
/* Phase B — manager-side hub. Backend: server/routes/advancing.js, mounted at
 * /api/shows/:id/advancing (protected; the PUBLIC per-token consultation links
 * are Phase C and live at /share/advancing/<token>). */

/** Which category a promoter advancing link covers. */
export type AdvancingShareScope = 'tech' | 'hotel' | 'logistics' | 'all';

/** Per-show advancing flags (show_advancing_settings). */
export interface AdvancingSettings {
  id?: number;
  show_id: number | string;
  /** When set, the band books the hotel itself → the hotel link is not needed. */
  hotel_booked_by_band: number;
  tech_enabled: number;
  hotel_enabled: number;
  logistics_enabled: number;
  /** Free-form confirmation deadlines per category ({ tech: '2026-09-01', ... }). */
  deadlines: Record<string, string>;
  /** Optional webhook URL notified on every promoter validation (fire-and-forget). */
  webhook_url?: string;
}

/** One advancing share link for a show (advancing_shares). */
export interface AdvancingShare {
  id: number;
  show_id: number | string;
  band_id: string | null;
  scope: AdvancingShareScope;
  token: string;
  enabled: number;
  created_at?: string;
  updated_at?: string;
}

/** One entry in the show's advancing exchange log (advancing_history). */
export interface AdvancingHistoryEntry {
  id: number;
  show_id: number | string;
  scope?: string;
  author?: string;
  message?: string;
  created_at?: string;
}

/** GET the show's advancing settings (defaults when none saved yet). */
export async function apiGetAdvancingSettings(showId: string | number): Promise<AdvancingSettings> {
  return fetchJSON(`${API_BASE}/shows/${showId}/advancing/settings`);
}

/** PUT the show's advancing settings (partial patch; only present fields change). */
export async function apiUpdateAdvancingSettings(
  showId: string | number,
  patch: Partial<AdvancingSettings>,
): Promise<AdvancingSettings> {
  return fetchJSON(`${API_BASE}/shows/${showId}/advancing/settings`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

/** GET the show's advancing share links. */
export async function apiGetAdvancingShares(showId: string | number): Promise<AdvancingShare[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/advancing/shares`);
}

/** POST a new advancing share link for a scope (idempotent per show+scope). */
export async function apiCreateAdvancingShare(
  showId: string | number,
  data: { scope: AdvancingShareScope },
): Promise<AdvancingShare> {
  return fetchJSON(`${API_BASE}/shows/${showId}/advancing/shares`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** DELETE one advancing share link. */
export async function apiDeleteAdvancingShare(
  showId: string | number,
  shareId: number,
): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/shows/${showId}/advancing/shares/${shareId}`, {
    method: 'DELETE',
  });
}

/** GET the show's advancing exchange history. */
export async function apiGetAdvancingHistory(showId: string | number): Promise<AdvancingHistoryEntry[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/advancing/history`);
}

/** POST an entry to the show's advancing exchange history. */
export async function apiPostAdvancingHistory(
  showId: string | number,
  data: { author: string; message: string; scope?: string },
): Promise<AdvancingHistoryEntry> {
  return fetchJSON(`${API_BASE}/shows/${showId}/advancing/history`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Per-scope advancement summary (Phase E dashboard). */
export interface AdvancingProgressScope {
  scope: AdvancingShareScope;
  /** How many items the show expects in this category (0 when disabled). */
  total: number;
  /** How many of those have been confirmed via any share link. */
  confirmed: number;
  /** How many have a non-empty remark. */
  remarked: number;
  /** 'complete' when total>0 and confirmed>=total, else 'incomplete'. */
  status: 'complete' | 'incomplete';
}

/** Payload of GET /shows/:id/advancing/progress. */
export interface AdvancingProgress {
  show_id: number | string;
  scopes: Record<'tech' | 'hotel' | 'logistics', AdvancingProgressScope>;
  global: AdvancingProgressScope;
}

/** GET the show's advancing progress dashboard (per scope + global roll-up). */
export async function apiGetAdvancingProgress(showId: string | number): Promise<AdvancingProgress> {
  return fetchJSON(`${API_BASE}/shows/${showId}/advancing/progress`);
}

/* ---- Public advancing share links (no auth — token in URL carries scope) ---- */

/** One demand/request item exposed to the organiser via a public advancing link. */
export interface AdvancingShareItem {
  itemKey: string;
  itemLabel: string;
  section: string;
  quantity?: number;
  detail?: string;
  provider?: string;
  confirmed: number;
  remarks: string;
  updated_at?: string | null;
}

/** Basic show context returned with a public advancing share. */
export interface AdvancingShareShow {
  id: number;
  name: string;
  date: string;
  time?: string;
  status?: string;
  venue?: string;
  city?: string;
  band_name?: string;
}

/** Payload of GET /api/share/advancing/:token (public). */
export interface AdvancingSharePublicDTO {
  token: string;
  scope: AdvancingShareScope;
  band: { id: string | null; name: string };
  show: AdvancingShareShow;
  settings: {
    hotel_booked_by_band: number;
    tech_enabled: number;
    hotel_enabled: number;
    logistics_enabled: number;
  };
  hotel_booked_by_band: number;
  /** Token of the show's public rider share (/share/rider/:token) — tech/all
      scopes only, null when the show has no rider or no rider share. */
  rider_share_token?: string | null;
  /** FULL RIDER VIEW (option A, 2026-10-24): the show's complete rider embedded
      in the SAME page (sections + items + input/output lists) — no second page
      needed. null for hotel/logistics scopes, locked shares, or no rider. */
  rider_view?: {
    token: string;
    transport: string;
    sections: Record<string, any>;
    items: AdvancingShareItem[];
    inputList: any[];
    outputList: any[];
  } | null;
  items: AdvancingShareItem[];
}

/** A saved validation row for a public advancing link. */
export interface AdvancingValidationRecord {
  id: number;
  share_token: string;
  item_key: string;
  item_label: string;
  scope: string;
  confirmed: number;
  remarks: string;
  quantity: number;
}

/** Fetch the public advancing share data for a link token (filtered by scope). */
export async function apiGetPublicAdvancingShare(token: string): Promise<AdvancingSharePublicDTO> {
  return fetchJSON(`${API_BASE}/share/advancing/${encodeURIComponent(token)}`);
}

/** Save a checkbox + remarks for one item on a public advancing link (no credentials). */
export async function apiValidateAdvancingShare(
  token: string,
  itemKey: string,
  confirmed: boolean,
  remarks: string,
): Promise<{ itemKey: string; itemLabel: string; confirmed: number; remarks: string; quantity: number }> {
  return fetchJSON(`${API_BASE}/share/advancing/${encodeURIComponent(token)}/validate`, {
    method: 'POST',
    body: JSON.stringify({ itemKey, confirmed, remarks }),
  });
}

/** Fetch the raw saved validations for a public advancing link. */
export async function apiGetAdvancingShareValidations(token: string): Promise<AdvancingValidationRecord[]> {
  return fetchJSON(`${API_BASE}/share/advancing/${encodeURIComponent(token)}/validations`);
}


/* ----------------------------- Tour: Venues ----------------------------- */

export interface VenueDTO {
  id: string;
  name: string;
  slug?: string;
  address?: string;
  city?: string;
  country?: string;
  zip?: string;
  state?: string;
  notes?: string;
  // Venue-level parking info (2026-10-11): garage, coordinates, emergency
  // numbers — shared by every show that plays here. Rendered in the
  // daysheet PDF "venue" section.
  parking?: string;
  capacity?: number;
  lat?: number;
  lng?: number;
  timezone?: string;
  electricity?: string;
  currency?: string;
  stage_size?: string;
  phone?: string;
  email?: string;
  website?: string;
}

export interface GeocodeResult {
  name: string;
  lat: number;
  lon: number;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zip?: string;
}

export async function apiGeocode(q: string, limit = 8): Promise<GeocodeResult[]> {
  return fetchJSON(`${API_BASE}/geocode/search?q=${encodeURIComponent(q)}&limit=${limit}`);
}

export async function apiGetVenues(): Promise<VenueDTO[]> {
  return fetchJSON(`${API_BASE}/venues`);
}

/** Online venue lookup (OpenStreetMap Nominatim) with day-sheet fields derived
 *  from the country (timezone, electricity, currency). */
export async function apiSearchVenues(q: string): Promise<VenueDTO[]> {
  return fetchJSON(`${API_BASE}/venues/search?q=${encodeURIComponent(q)}`);
}
/** Resolve contact info (phone/email/website) for one online candidate. */
export async function apiGetVenueContacts(osmType?: string, osmId?: string): Promise<{ phone?: string; email?: string; website?: string }> {
  if (!osmType || !osmId) return {};
  return fetchJSON(`${API_BASE}/venues/contacts?osm_type=${encodeURIComponent(osmType)}&osm_id=${encodeURIComponent(osmId)}`);
}
export async function apiCreateVenue(data: Partial<VenueDTO>): Promise<VenueDTO> {
  return fetchJSON(`${API_BASE}/venues`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateVenue(id: string, data: Partial<VenueDTO>): Promise<VenueDTO> {
  return fetchJSON(`${API_BASE}/venues/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteVenue(id: string): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/venues/${id}`, { method: 'DELETE' });
}

/* ----------------------------- Tour: Contacts ----------------------------- */

export type ContactRole = string; // now free-text: roles are user-defined tags

export interface ContactDTO {
  id: string;
  name: string;
  lastName?: string;
  role?: ContactRole;
  function?: string;
  email?: string;
  phone?: string;
  company?: string;
  photo_url?: string;
  venue_id?: string;
  band_id?: string;
  notes?: string;
  /** Bands this contact is visible in (empty = shared/global contact). */
  band_ids?: string[];
}

export async function apiGetContacts(): Promise<ContactDTO[]> {
  const rows = await fetchJSON(`${API_BASE}/contacts`);
  // Normalize legacy DB shape (last_name) to the DTO (lastName).
  return (rows as ContactDTO[]).map((c) => ({
    ...c,
    lastName: (c as any).lastName ?? (c as any).last_name ?? '',
    band_ids: (c as any).band_ids ?? [],
  }));
}
export async function apiCreateContact(data: Partial<ContactDTO>): Promise<ContactDTO> {
  return fetchJSON(`${API_BASE}/contacts`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateContact(id: string, data: Partial<ContactDTO>): Promise<ContactDTO> {
  return fetchJSON(`${API_BASE}/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteContact(id: string): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/contacts/${id}`, { method: 'DELETE' });
}
/** Assign a contact to a band (editor+ on that band required). */
export async function apiAddContactBand(id: string, bandId: string): Promise<{ linked: boolean; band_ids: string[] }> {
  return fetchJSON(`${API_BASE}/contacts/${id}/bands`, { method: 'PUT', body: JSON.stringify({ band_id: bandId }) });
}
/** Remove a contact from a band — removing the last link makes it global. */
export async function apiRemoveContactBand(id: string, bandId: string): Promise<{ deleted: boolean; band_ids: string[] }> {
  return fetchJSON(`${API_BASE}/contacts/${id}/bands/${bandId}`, { method: 'DELETE' });
}

export interface TmContactPreview {
  name: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  role?: string;
  function?: string;
  dob?: string;
  photo?: string;
  tmVenueId?: string | null;
  suggestedVenueId?: string | null;
}
export async function apiGetTmContacts(): Promise<{ contacts: TmContactPreview[]; existingRoles: string[] }> {
  return fetchJSON(`${API_BASE}/tm/contacts`);
}
export async function apiImportTmContacts(items: Partial<ContactDTO>[]): Promise<{ created: number; items: unknown[] }> {
  return fetchJSON(`${API_BASE}/tm/import-contacts`, { method: 'POST', body: JSON.stringify({ items }) });
}

export interface TmShowPreview {
  tmId: string;
  name: string;
  date: string;
  time: string;
  event_type: string;
  status: string;
  band_id?: string | null;
  notes?: string;
  tmType: string;
}
export async function apiGetTmShows(): Promise<{ shows: TmShowPreview[] }> {
  return fetchJSON(`${API_BASE}/tm/shows`);
}
export async function apiImportTmShows(items: TmShowPreview[], includeOld?: boolean): Promise<{ ok: boolean; created: number; skipped?: number; filtered?: number; total?: number }> {
  return fetchJSON(`${API_BASE}/tm/import-shows`, { method: 'POST', body: JSON.stringify({ items, includeOld }) });
}
export async function apiImportTmTimezones(): Promise<{ ok: boolean; summary: Record<string, number>; events: number }> {
  return fetchJSON(`${API_BASE}/tm/import-timezones`, { method: 'POST' });
}

export interface TmNewShow {
  tmId: string;
  date: string;
  name: string;
  band_id?: string | null;
  band?: string;
  status: string;
  timeZone?: string;
  tmType?: string;
  suggested_event_type?: string;
}
export async function apiGetTmNewShows(): Promise<{ ok: boolean; count: number; candidates: TmNewShow[] }> {
  return fetchJSON(`${API_BASE}/tm/new-shows`);
}

export interface TmHotelPreview {
  blockId: string;
  eventIri: string;
  date: string;
  band?: string;
  name: string;
  tmType?: string;
  hotelName: string;
  address: string;
  confirmation: string;
  notes: string;
  action: 'no-show' | 'create' | 'exists' | 'fill';
  showId?: number | null;
  showName?: string | null;
  accommodationId?: number | null;
}
export async function apiGetTmHotels(): Promise<{ ok: boolean; totalBlocks: number; previews: TmHotelPreview[]; count: number }> {
  return fetchJSON(`${API_BASE}/tm/hotels`);
}
export async function apiImportTmHotels(): Promise<{ ok: boolean; summary: Record<string, number> }> {
  return fetchJSON(`${API_BASE}/tm/import-hotels`, { method: 'POST' });
}

/* ----------------------------- Tour: Shows ----------------------------- */

export interface ShowDTO {
  id: string;
  name?: string;
  slug?: string;
  band_id?: string;
  venue_id?: string;
  date?: string;
  status?: string;
  event_type?: string;
  // How the band travels for this show (van/plane/tourbus/train).
  transport?: string;
  // For a travel day: the show being travelled to (its rider gives the transport).
  destination_show_id?: string | null;
  destination_transport?: string;
  notes?: string;
  rider_id?: string | null;
  // Tour this show belongs to (nullable — a show may be standalone).
  tour_id?: string | null;
  // The setlist that will be played for this show (1 setlist per show).
  setlist_id?: number | null;
  // The setlist ACTUALLY played (editable copy of the planned one).
  played_setlist_id?: number | null;
  // The checklist used for this show (reusable band checklist).
  checklist_id?: number | null;
  guestlist?: string;
  accommodations?: string;
  roadmap?: string;
  // ---- Finance (deal side) ----
  // Amounts keep the currency they were agreed in; no automatic FX conversion.
  guarantee?: number;
  currency?: string;
  fee_type?: string;
  deposit_amount?: number;
  deposit_received?: number;
  payment_status?: string;
  payment_due_date?: string;
  payment_notes?: string;
  per_diem_amount?: number;
  per_diem_people?: number;
  capacity?: number;
  // ---- Itinerary / tour-manager day info ----
  timezone?: string;
  electricity?: string;
  wifi_venue?: string;
  luggage_notes?: string;
  running_order?: string;
  welcome_note?: string;
  currency_note?: string;
  artist_pass?: string;
  passport_reminder?: string;
  stage_size?: string;
  stage_name?: string;
  currency_rate?: string;
  duration_minutes?: number;
  door_time?: string;
  weather?: string;
  // Flights notes (free text, shown on the day sheet).
  flight_notes?: string;
  /** Ville de départ d'un TRAVEL day (2026-10-17) : le type travel n'a pas de venue. */
  from_city?: string;
  /** Ville d'arrivée d'un TRAVEL day : pré-remplie depuis la venue du show de destination, éditable. */
  to_city?: string;
  /** Aéroport du travel day en mode plane : pré-rempli depuis bands.default_airport, éditable. */
  airport_iata?: string;
  tickets_sold?: number;
  // expanded lookups from backend
  band_name?: string;
  venue_name?: string;
  city?: string;
  country?: string;
  tour_name?: string;
}

export interface GuestDTO {
  id: string;
  tour_date_id?: string;
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  company?: string;
  notes?: string;
  check_in_status?: string;
  arrival_time?: string;
}

export interface RoadmapItemDTO {
  id: string;
  tour_date_id?: string;
  time_from?: string;
  time_to?: string;
  category?: string;
  activity: string;
  responsible?: string;
  contact_phone?: string;
  location?: string;
  duration?: number;
  notes?: string;
  sort_order?: number;
}

export interface MemberFlightDTO {
  id: string;
  tour_date_id?: string;
  team_member_id?: string;
  /** IDs of the members on this flight/booking (multi-person). */
  member_ids?: string;
  person_name?: string;
  flight_date?: string;
  from_city?: string;
  from_iata?: string;
  to_city?: string;
  to_iata?: string;
  from_station?: string;
  to_station?: string;
  departure?: string;
  arrival?: string;
  airline?: string;
  flight_no?: string;
  booking_ref?: string;
  notes?: string;
  duration?: string;
  /** Transport mode: flight | train | ferry (defaults to 'flight'). */
  mode?: string;
  train_class?: string;
  /** Coach / wagon number (train legs, SPEC-TRAIN-LEG-FIXES). */
  coach?: string;
  seat?: string;
  platform?: string;
  /** Station coordinates PERSISTED on the leg row (geocoded at save when the
   * station is not in the `stations` catalog). Written by the server only. */
  from_station_lat?: number | null;
  from_station_lng?: number | null;
  to_station_lat?: number | null;
  to_station_lng?: number | null;
  sort_order?: number;
}

/** Airport catalog candidate (From/To autocomplete). */
export interface AirportDTO {
  iata: string;
  name: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
}
export async function apiSearchAirports(q: string): Promise<AirportDTO[]> {
  return fetchJSON(`${API_BASE}/airports/search?q=${encodeURIComponent(q)}`);
}
// IANA timezone per IATA (2026-10-18) — flight duration auto-compute.
export async function apiGetAirportTimezones(iatas: string[]): Promise<Record<string, string>> {
  const list = iatas.filter(Boolean).join(',');
  if (!list) return {};
  return fetchJSON(`${API_BASE}/airports/tz?iata=${encodeURIComponent(list)}`);
}
export interface AirlineDTO { iata?: string; name: string; alliance?: string }
export async function apiSearchAirlines(q: string): Promise<AirlineDTO[]> {
  return fetchJSON(`${API_BASE}/airlines/search?q=${encodeURIComponent(q)}`);
}

export async function apiGetShows(params?: { band?: string; status?: string; minDate?: string }): Promise<ShowDTO[]> {
  const query = new URLSearchParams();
  if (params?.band) query.set('band', params.band);
  if (params?.status) query.set('status', params.status);
  if (params?.minDate) query.set('min_date', params.minDate);
  const qs = query.toString();
  return fetchJSON(`${API_BASE}/shows${qs ? `?${qs}` : ''}`);
}
/** Default cut-off for show lists: rolling one year back — older shows are archives. */
export function showsMinDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
}
/** Cut-off for the Atlas map by default: UPCOMING only (user 2026-10-22). */
export function todayMinDate(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
export async function apiGetShow(id: string): Promise<ShowDTO> {
  return fetchJSON(`${API_BASE}/shows/${id}`);
}
// Raw .ics download (group subscription calendar). Requires the band's secret
// calendar_token (Team members only) — the server 403s without a valid token.
export async function apiGetBandShowsIcs(bandId: string, token?: string): Promise<Blob> {
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  const res = await fetch(`${API_BASE}/calendar/bands/${bandId}/shows.ics${q}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to load calendar (${res.status})`);
  return res.blob();
}
export async function apiGetShowIcs(showId: string, token?: string): Promise<Blob> {
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  const res = await fetch(`${API_BASE}/calendar/shows/${showId}.ics${q}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to load .ics (${res.status})`);
  return res.blob();
}
export async function apiCreateShow(data: Partial<ShowDTO>): Promise<ShowDTO> {
  return fetchJSON(`${API_BASE}/shows`, { method: 'POST', body: JSON.stringify(data) });
}

// --- Show guests (structured guestlist) ---
export async function apiGetShowGuests(showId: string): Promise<GuestDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/guests`);
}
export async function apiCreateShowGuest(showId: string, data: Partial<GuestDTO>): Promise<GuestDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/guests`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowGuest(showId: string, guestId: string, data: Partial<GuestDTO>): Promise<GuestDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/guests/${guestId}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowGuest(showId: string, guestId: string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/guests/${guestId}`, { method: 'DELETE' });
}

// --- Show roadmap (day schedule) ---
export async function apiGetShowRoadmap(showId: string): Promise<RoadmapItemDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/roadmap`);
}
export async function apiCreateShowRoadmapItem(showId: string, data: Partial<RoadmapItemDTO>): Promise<RoadmapItemDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/roadmap`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowRoadmapItem(showId: string, itemId: string, data: Partial<RoadmapItemDTO>): Promise<RoadmapItemDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/roadmap/${itemId}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowRoadmapItem(showId: string, itemId: string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/roadmap/${itemId}`, { method: 'DELETE' });
}

// --- Show Time persistent check-in (phase 2, 2026-09-05) ---
export interface ShowCheckinDTO {
  roadmap_item_id: string;
  activity: string;
  category?: string;
  time_from?: string;
  responsible?: string;
  duration?: number;
  sort_order?: number;
  done: number;
  checkin_id?: string;
}
export async function apiGetShowCheckins(showId: string): Promise<ShowCheckinDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/checkins`);
}
export async function apiSetShowCheckin(showId: string, roadmapItemId: string, done: number): Promise<ShowCheckinDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/checkins/${roadmapItemId}`, { method: 'PUT', body: JSON.stringify({ done }) });
}

// --- Per-person flights for a tour day ---
export async function apiGetShowFlights(showId: string): Promise<MemberFlightDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/flights`);
}
export async function apiCreateShowFlight(showId: string, data: Partial<MemberFlightDTO>): Promise<MemberFlightDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/flights`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowFlight(showId: string, id: string, data: Partial<MemberFlightDTO>): Promise<MemberFlightDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/flights/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowFlight(showId: string, id: string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/flights/${id}`, { method: 'DELETE' });
}

// --- Unified transports (flight | train | ferry) for a tour day ---
export async function apiGetShowTransports(showId: string): Promise<MemberFlightDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/transports`);
}
export async function apiCreateShowTransport(showId: string, data: Partial<MemberFlightDTO>): Promise<MemberFlightDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/transports`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowTransport(showId: string, id: string, data: Partial<MemberFlightDTO>): Promise<MemberFlightDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/transports/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowTransport(showId: string, id: string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/transports/${id}`, { method: 'DELETE' });
}

// --- Flight tracking: live status + auto-fill lookup ----------------------
// Both endpoints are server→provider calls (AeroDataBox). `available:false`
// means no provider key is configured (reason 'no_key') or the flight could
// not be resolved — the UI MUST degrade silently (no badge / discreet note),
// never throw. HTTP 200 is returned in both available and unavailable cases.
export interface FlightStatusDTO {
  available: boolean;
  reason?: string;
  status?: string;
  status_raw?: string;
  scheduled_dep?: string;
  estimated_dep?: string;
  actual_dep?: string;
  scheduled_arr?: string;
  estimated_arr?: string;
  actual_arr?: string;
  gate?: string;
  terminal?: string;
  arrival_gate?: string;
  arrival_terminal?: string;
  airline?: string;
  from_iata?: string;
  to_iata?: string;
  from_city?: string;
  to_city?: string;
}

/** Live status of one flight (on time / delayed / cancelled + est. time + gate). */
export async function apiGetFlightStatus(flightNo: string, date: string): Promise<FlightStatusDTO> {
  const qs = new URLSearchParams({ flight_no: flightNo, date });
  return fetchJSON(`${API_BASE}/flights/status?${qs.toString()}`);
}

/** Auto-fill lookup for a flight (route / airline / scheduled times). */
export async function apiGetFlightLookup(flightNo: string, date: string): Promise<FlightStatusDTO> {
  const qs = new URLSearchParams({ flight_no: flightNo, date });
  return fetchJSON(`${API_BASE}/flights/lookup?${qs.toString()}`);
}

// --- Contact persons for the day sheet (stage manager, backstage, runners…) ---
export interface ShowContactDTO {
  id: string;
  tour_date_id?: string;
  role?: string;
  name?: string;
  phone?: string;
  email?: string;
  notes?: string;
  sort_order?: number;
}
export async function apiGetShowContacts(showId: string): Promise<ShowContactDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/contacts`);
}
export async function apiCreateShowContact(showId: string, data: Partial<ShowContactDTO>): Promise<ShowContactDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/contacts`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowContact(showId: string, id: string, data: Partial<ShowContactDTO>): Promise<ShowContactDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowContact(showId: string, id: string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/contacts/${id}`, { method: 'DELETE' });
}

// --- Show notes: tech_notes / misc_notes / notes (per show day) ---
// Three sibling resources backed by makeResource (server/routes/tour-extra.js) —
// same CRUD shape as contacts: GET list, POST create, PUT update, DELETE remove.
// Each is a separate DAYSHEET section (tech → TECH tab; misc + notes → LOGISTICS),
// so the user chooses in the Daysheet Layout which notes print.

export interface ShowTechNoteDTO {
  id: number | string;
  tour_date_id: number | string;
  content: string;
}
export async function apiGetShowTechNotes(showId: string): Promise<ShowTechNoteDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/tech-notes`);
}
export async function apiCreateShowTechNote(showId: string, data: Partial<ShowTechNoteDTO>): Promise<ShowTechNoteDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/tech-notes`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowTechNote(showId: string, id: string | number, data: Partial<ShowTechNoteDTO>): Promise<ShowTechNoteDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/tech-notes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowTechNote(showId: string, id: string | number): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/tech-notes/${id}`, { method: 'DELETE' });
}

export interface ShowMiscNoteDTO {
  id: number | string;
  tour_date_id: number | string;
  category?: string;
  content: string;
}
export async function apiGetShowMiscNotes(showId: string): Promise<ShowMiscNoteDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/misc-notes`);
}
export async function apiCreateShowMiscNote(showId: string, data: Partial<ShowMiscNoteDTO>): Promise<ShowMiscNoteDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/misc-notes`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowMiscNote(showId: string, id: string | number, data: Partial<ShowMiscNoteDTO>): Promise<ShowMiscNoteDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/misc-notes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowMiscNote(showId: string, id: string | number): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/misc-notes/${id}`, { method: 'DELETE' });
}

export interface ShowCategorizedNoteDTO {
  id: number | string;
  tour_date_id: number | string;
  category?: string;
  title?: string;
  content: string;
}
export async function apiGetShowCategorizedNotes(showId: string): Promise<ShowCategorizedNoteDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/notes`);
}
export async function apiCreateShowCategorizedNote(showId: string, data: Partial<ShowCategorizedNoteDTO>): Promise<ShowCategorizedNoteDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/notes`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowCategorizedNote(showId: string, id: string | number, data: Partial<ShowCategorizedNoteDTO>): Promise<ShowCategorizedNoteDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/notes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowCategorizedNote(showId: string, id: string | number): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/notes/${id}`, { method: 'DELETE' });
}

export interface DistanceDTO {
  id: number;
  tour_date_id: number;
  from_label: string;
  to_label: string;
  mode: string;
  distance: string;
  duration: string;
  notes: string;
  sort_order?: number;
}
export async function apiGetShowDistances(showId: string): Promise<DistanceDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/distances`);
}
export async function apiCreateShowDistance(showId: string, data: Partial<DistanceDTO>): Promise<DistanceDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/distances`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowDistance(showId: string, id: number, data: Partial<DistanceDTO>): Promise<DistanceDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/distances/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowDistance(showId: string, id: number): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/distances/${id}`, { method: 'DELETE' });
}
export interface DistanceCalcResult {
  accommodation_id?: number;
  hotel_name?: string;
  venue_km: number | null;
  venue_min: number | null;
  airport_km: number | null;
  airport_min: number | null;
  station_km: number | null;
  station_min: number | null;
  // HOME legs (2026-10-20): departure-side distances from the band's Home
  // base, per transport mode (plane -> airport, train -> station,
  // van/tourbus -> hotel). Null = leg absent (no invented estimate).
  home_airport_km?: number | null;
  home_airport_min?: number | null;
  home_station_km?: number | null;
  home_station_min?: number | null;
  home_hotel_km?: number | null;
  home_hotel_min?: number | null;
}
export interface DistanceCalcBandLeg {
  from_label: string;
  to_label: string;
  mode: string;
  km: number;
  min: number | null;
}
export interface DistanceCalcResponse {
  results: DistanceCalcResult[];
  venue_airport_km: number | null;
  venue_airport_min: number | null;
  venue_station_km: number | null;
  venue_station_min: number | null;
  station: { lat: number; lng: number; label: string } | null;
  geocoded: number[];
  // Band -> venue leg (2026-10-11): present only when the band has a Home base
  // (bands.lat/lng). Null + a readable reason otherwise — no phantom row.
  band_leg?: DistanceCalcBandLeg | null;
  band_leg_reason?: string | null;
  // HOME legs summary (2026-10-20): resolved departure point + van-only
  // home->venue fallback. Null = leg absent (no invented estimate).
  departure_airport?: { iata: string; name: string; city: string; lat: number; lng: number } | null;
  departure_station?: { uic: string; name: string; city: string; lat: number; lng: number } | null;
  // Resolved departure CODES (2026-11-02, LOT U2c): '' when the leg has no
  // departure point. Tells which band column fed the leg.
  home_airport_code?: string;
  home_station_code?: string;
  home_venue_km?: number | null;
  home_venue_min?: number | null;
  home_leg_reason?: string | null;
}
export async function apiCalculateDistances(showId: string, body: { transport?: string; venue?: { lat?: number; lng?: number; city?: string; name?: string } }): Promise<DistanceCalcResponse> {
  return fetchJSON(`${API_BASE}/shows/${showId}/distances/calculate`, { method: 'POST', body: JSON.stringify(body) });
}

// --- Train-station catalog (2026-10-20, gares lot) — mode 'rail' ---
export interface StationDTO {
  uic: string;
  name: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
}
/** Train-station catalog search (UIC / name, LIMIT 10). */
export async function apiSearchStations(q: string): Promise<StationDTO[]> {
  return fetchJSON(`${API_BASE}/stations/search?q=${encodeURIComponent(q)}`);
}
export async function apiUpdateShow(id: string, data: Partial<ShowDTO>): Promise<ShowDTO> {
  return fetchJSON(`${API_BASE}/shows/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

// --- Full itinerary aggregate for a band (all days) ---
export interface ItineraryBandResponse {
  band: any;
  days: {
    show: ShowDTO;
    roadmap: RoadmapItemDTO[];
    flights: MemberFlightDTO[];
    rooming: RoomingDTO[];
    accommodations: AccommodationDTO[];
    guests: GuestDTO[];
    team: TeamMemberDTO[];
  }[];
}
export async function apiGetItinerary(bandId: string): Promise<ItineraryBandResponse> {
  return fetchJSON(`${API_BASE}/itinerary/${bandId}`);
}
export async function apiDeleteShow(id: string): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/shows/${id}`, { method: 'DELETE' });
}

/* ----------------------------- Show: Accommodations ----------------------------- */

export interface AccommodationDTO {
  id: number;
  tour_date_id: number;
  hotel_name: string;
  address?: string;
  zip?: string;
  city?: string;
  country?: string;
  phone?: string;
  email?: string;
  confirmation_number?: string;
  check_in?: string;
  check_out?: string;
  room_type?: string;
  notes?: string;
  website?: string;
  distance_airport_km?: string;
  distance_venue_km?: string;
  amenity_wifi?: number;
  amenity_pool?: number;
  amenity_fitness?: number;
  amenity_restaurant?: number;
  lat?: number;
  lng?: number;
}
export interface RoomingDTO {
  id: number;
  tour_date_id: number;
  team_member_id: number;
  role_at_date?: string;
  accommodation_id?: number | null;
  arrival?: string;
  departure?: string;
  room_number?: string;
  room_comment?: string;
  notes?: string;
}
export async function apiGetAccommodations(showId: string): Promise<AccommodationDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/accommodations`);
}
export async function apiCreateAccommodation(showId: string, data: Partial<AccommodationDTO>): Promise<AccommodationDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/accommodations`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateAccommodation(showId: string, id: string, data: Partial<AccommodationDTO>): Promise<AccommodationDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/accommodations/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteAccommodation(showId: string, id: string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/accommodations/${id}`, { method: 'DELETE' });
}
export async function apiGetRooming(showId: string): Promise<RoomingDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/rooming`);
}
export async function apiCreateRooming(showId: string, data: Partial<RoomingDTO>): Promise<RoomingDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/rooming`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateRooming(showId: string, id: string, data: Partial<RoomingDTO>): Promise<RoomingDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/rooming/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteRooming(showId: string, id: string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/rooming/${id}`, { method: 'DELETE' });
}

/* ----------------------------- Tour: Calendar ----------------------------- */

export async function apiGetCalendar(): Promise<any[]> {
  return fetchJSON(`${API_BASE}/calendar`);
}

/* ----------------------------- Tour: Tours ----------------------------- */

export interface TourDTO {
  id: string;
  name: string;
  band_id?: string | null;
  slug?: string;
  notes?: string;
  /** Per-tour public promo-page blob (option C, 2026-10-19) — JSON string. */
  promo_content?: string | null;
}

/** List tours the current user may see (filtered to accessible bands). */
export async function apiGetTours(): Promise<TourDTO[]> {
  return fetchJSON(`${API_BASE}/tours`);
}

/** Create a tour for a band (editor+ required). */
export async function apiGetTour(id: string | number): Promise<TourDTO> {
  return fetchJSON(`${API_BASE}/tours/${encodeURIComponent(String(id))}`);
}
export async function apiCreateTour(data: { name: string; band_id?: string | null; notes?: string }): Promise<TourDTO> {
  return fetchJSON(`${API_BASE}/tours`, { method: 'POST', body: JSON.stringify(data) });
}

/** Delete a tour. The backend refuses when the tour still has shows. */
export async function apiDeleteTour(id: string | number): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/tours/${encodeURIComponent(String(id))}`, { method: 'DELETE' });
}

/** Fetch the full aggregated tour-book payload for a tour (dates + daysheet
 * data per show). Used to build the combined Tour Book PDF. (2026-08-11) */
export async function apiGetTourBook(id: string | number): Promise<any> {
  return fetchJSON(`${API_BASE}/tours/${encodeURIComponent(String(id))}/book`);
}

/** One currency row of a tour's aggregated budget. */
export interface TourBudgetRow {
  currency: string;
  revenue: number;
  expenses: number;
  net: number;
}

/** A tour's forecast-vs-actual budget, aggregated from its shows' finance.
 * Revenues = the shows' guarantees (in each show's currency); expenses =
 * show_expenses (in each expense's own currency). Amounts are never converted. */
export interface TourBudgetDTO {
  tour: { id: string | number; name: string; band_id?: string | null; slug?: string };
  byCurrency: TourBudgetRow[];
}

/** Fetch the per-currency budget (revenue / expenses / net) for a tour. */
export async function apiGetTourBudget(id: string | number): Promise<TourBudgetDTO> {
  return fetchJSON(`${API_BASE}/tours/${encodeURIComponent(String(id))}/budget`);
}

/* ----------------------------- Tour: Setlists ----------------------------- */

export interface SetlistSongDTO {
  id: string;
  title: string;
  artist?: string;
  keys?: string;
  tempo?: string;
  duration?: string;
  order: number;
  position?: number;
  notes?: string;
  remarks?: string;
  is_separator?: boolean;
  /** Optional trace back to the songbook (songs) row this line was inserted
   * from. NULL = free-form song (legacy behaviour). Metadata stays COPIED, so
   * the setlist line remains independently editable. (Smart Setlist V1) */
  song_id?: number | null;
}

export interface SetlistDTO {
  id: string;
  band_id: string;
  name: string;
  date?: string;
  show_id?: number | null;
  notes?: string;
  songs: SetlistSongDTO[];
}

/** Which columns appear in the exported setlist PDF. All default to true. */
export interface SetlistPdfColumns {
  showNumber: boolean;
  title: boolean;
  remarks: boolean;
  duration: boolean;
  tempo: boolean;
  key: boolean;
  /** Show the table's header row (#, Title, …). Off = start straight at song 1. */
  showTableHeader: boolean;
}

export function defaultSetlistPdfColumns(): SetlistPdfColumns {
  return { showNumber: true, title: true, remarks: true, duration: true, tempo: true, key: true, showTableHeader: true };
}

/** Style options for the setlist PDF (grid, fonts, case, zebra contrast…). */
export interface SetlistPdfStyle {
  showGrid: boolean;
  bodyFontSize: number;
  uppercase: boolean;
  zebraFill: string;   // fill color of odd/even song rows
  headerFill: string;  // fill color of the header row
  multiPage: boolean;  // allow the setlist to flow over multiple pages (with page footer)
  showLogo: boolean;   // render the band logo in the PDF header
  logoWidth: number;   // logo width in pt (≈ half printable width = 257)
}

export function defaultSetlistPdfStyle(): SetlistPdfStyle {
  return { showGrid: true, bodyFontSize: 11, uppercase: false, zebraFill: '#f4f4f4', headerFill: '#f2f2f2', multiPage: false, showLogo: true, logoWidth: 257 };
}

export async function apiGetSetlists(bandId: string): Promise<SetlistDTO[]> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/setlists`);
}
export async function apiGetSetlistsUpcomingShow(bandId: string): Promise<{
  id: number;
  name: string;
  date: string;
  venue_name?: string;
  venue_city?: string;
  tour_name?: string;
} | null> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/setlists/upcoming-show`);
}
export async function apiCreateSetlist(bandId: string, data: Partial<SetlistDTO>): Promise<SetlistDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/setlists`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateSetlist(bandId: string, id: string, data: Partial<SetlistDTO>): Promise<SetlistDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/setlists/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDuplicateSetlist(bandId: string, id: string, name?: string): Promise<SetlistDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/setlists/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify(name ? { name } : {}),
  });
}
export async function apiDeleteSetlist(bandId: string, id: string): Promise<{ success: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/setlists/${id}`, { method: 'DELETE' });
}

/* ----------------------------- Songbook (songs) ----------------------------- */

export interface SongDTO {
  id: string;
  band_id: string;
  title: string;
  artist?: string;
  keys?: string;
  tempo?: string;
  duration?: string;
  notes?: string;
  position: number;
  created_at?: string;
  updated_at?: string;
}

/** List the band's songbook, ordered by position. */
export async function apiGetSongs(bandId: string): Promise<SongDTO[]> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs`);
}
/** Create a song in the band's songbook. */
export async function apiCreateSong(bandId: string, data: Partial<SongDTO>): Promise<SongDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs`, { method: 'POST', body: JSON.stringify(data) });
}
/** Update a song in the band's songbook. */
export async function apiUpdateSong(bandId: string, songId: string, data: Partial<SongDTO>): Promise<SongDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs/${songId}`, { method: 'PUT', body: JSON.stringify(data) });
}
/** Delete a song from the songbook. Existing setlist lines that reference it
 * keep their copied metadata; song_id is nulled via the FK ON DELETE SET NULL. */
export async function apiDeleteSong(bandId: string, songId: string): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs/${songId}`, { method: 'DELETE' });
}
/** Reorder the songbook by an explicit id array (drag-to-reorder). */
export async function apiReorderSongs(bandId: string, orderedIds: string[]): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs/reorder`, {
    method: 'PUT', body: JSON.stringify({ orderedIds }),
  });
}

/* ----------------------------- Song charts (per instrument) ----------------------------- */
// A chart is one free-text part per song × instrument ("the bassist sees only
// the bass charts"). Instrument is a FREE name (matches rider card
// instruments[].name) — it is the join key with "the instruments a musician
// plays", so it is always URL-encoded in paths and never given a stable id.
// (Smart Setlist V2, backend verified.)

export interface SongChart {
  id: string;
  song_id: string;
  band_id?: string;
  instrument: string;
  content: string;
  updated_at?: string;
}

/** A chart row returned across a band's whole songbook (by-instrument query),
 * enriched with the song's title for list / per-account views. */
export interface SongChartByInstrument {
  song_id: string;
  song_title: string;
  instrument: string;
  content: string;
}

/** List every chart of one song (viewer+ on the band). */
export async function apiGetSongCharts(bandId: string, songId: string): Promise<SongChart[]> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs/${songId}/charts`);
}

/** Upsert a chart for a song × instrument (editor+). A second POST/PUT for the
 * same instrument updates instead of duplicating (UNIQUE song+band+instrument). */
export async function apiUpsertSongChart(
  bandId: string,
  songId: string,
  data: { instrument: string; content: string },
): Promise<SongChart> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs/${songId}/charts`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Delete the chart for one instrument on a song (editor+). */
export async function apiDeleteSongChart(
  bandId: string,
  songId: string,
  instrument: string,
): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs/${songId}/charts/${encodeURIComponent(instrument)}`, {
    method: 'DELETE',
  });
}

/** Charts of the band's WHOLE songbook for the given instruments — used by the
 * Songbook "filter by instrument". The server spans the whole songbook from a
 * single song-scoped route; `:sid` is only a URL requirement there, so we use a
 * sentinel and rely on the band-scoped whole-songbook result. Returns
 * [{song_id, song_title, instrument, content}] ordered by song position. */
export async function apiGetSongChartsByInstrument(
  bandId: string,
  instruments: string[],
): Promise<SongChartByInstrument[]> {
  if (instruments.length === 0) return [];
  const qs = instruments.map((i) => encodeURIComponent(i)).join(',');
  return fetchJSON(`${API_BASE}/bands/${bandId}/songs/all/charts/by-instrument?instruments=${qs}`);
}

/** The logged-in user's OWN song charts (Smart Setlist V2 "view by account"):
 * songbook charts of the given band (or every accessible band when omitted)
 * whose instrument is among the instruments the user plays on their rider
 * cards. Read-only + self-scoped. Returns
 * [{band_id, band_name, song_id, song_title, instrument, content}]. */
export interface MySongChartDTO {
  band_id: string;
  band_name?: string;
  song_id: string;
  song_title: string;
  instrument: string;
  content: string;
}
export async function apiGetMySongCharts(bandId?: string): Promise<MySongChartDTO[]> {
  const q = bandId ? `?band=${encodeURIComponent(bandId)}` : '';
  return fetchJSON(`${API_BASE}/me/song-charts${q}`);
}

/* ----------------------------- Checklists ----------------------------- */

export interface ChecklistItemDTO {
  id: string;
  text: string;
  assigned_to?: string;
  done: boolean;
  // Checklists V2-B: the assignee as a team_member id (NULL = unassigned).
  // `assigned_to` stays for legacy textual assignees.
  assignee_id?: string | null;
  // Who ticked the item (team_member id) and when — set by the server.
  done_by?: string | null;
  done_at?: string | null;
}

export interface ChecklistDTO {
  id: string;
  band_id: string;
  name: string;
  notes?: string;
  items: ChecklistItemDTO[];
  // Checklists V2-B: 'band' (library list) | 'personal' (owned by a member).
  scope?: 'band' | 'personal';
  // Grouping category: departure / changeover / soundcheck / loadout / custom.
  category?: string;
  // For personal lists: the owning team_member id.
  owner_member_id?: string | null;
  // Present on rows returned by apiGetShowChecklists (linked to that show).
  linked_to_show?: boolean;
  legacy_link?: boolean;
}

export async function apiGetChecklists(bandId: string): Promise<ChecklistDTO[]> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/checklists`);
}
// Checklists V2-B: the checklists LINKED to a show (join table
// tour_date_checklists), server-filtered for personal-scope visibility.
export async function apiGetShowChecklists(bandId: string, showId: string | number): Promise<ChecklistDTO[]> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/checklists?showId=${encodeURIComponent(String(showId))}`);
}
// Link / unlink a checklist to a show (POST/DELETE tour_date_checklists).
export async function apiLinkChecklistToShow(bandId: string, checklistId: string | number, showId: string | number): Promise<{ linked: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/checklists/${checklistId}/shows/${showId}`, { method: 'POST', body: JSON.stringify({}) });
}
export async function apiUnlinkChecklistFromShow(bandId: string, checklistId: string | number, showId: string | number): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/checklists/${checklistId}/shows/${showId}`, { method: 'DELETE' });
}
// Self-service tick (Checklists V2-B): a NON-editor member ticks HIS OWN
// assigned item. Route mounted under /api/me (before the read-only guard).
export async function apiSetMyChecklistItemDone(itemId: string | number, done: boolean): Promise<ChecklistItemDTO> {
  return fetchJSON(`${API_BASE}/me/checklist-items/${itemId}/done`, { method: 'PATCH', body: JSON.stringify({ done }) });
}
export async function apiCreateChecklist(bandId: string, data: Partial<ChecklistDTO>): Promise<ChecklistDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/checklists`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateChecklist(bandId: string, id: string, data: Partial<ChecklistDTO>): Promise<ChecklistDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/checklists/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDuplicateChecklist(bandId: string, id: string, name?: string): Promise<ChecklistDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/checklists/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify(name ? { name } : {}),
  });
}
export async function apiDeleteChecklist(bandId: string, id: string): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/checklists/${id}`, { method: 'DELETE' });
}

/* ----------------------------- Health ----------------------------- */

export async function apiHealth(): Promise<boolean> {
  try {
    await fetchJSON(`${API_BASE}/health`);
    return true;
  } catch {
    return false;
  }
}

/* ----------------------------- Auth ----------------------------- */

export interface AuthUser {
  id: number;
  username: string;
  role: 'admin' | 'manager' | 'read-only';
  /** Tenant model: 'superadmin' runs the platform; everyone else is band-scoped.
   * Sent by the server (snake_case) in the login / /auth/me payloads. */
  global_role?: 'superadmin' | 'user';
  /** Showcase (demo) account: browses sanitized sample data (fixtures) on the
   * admin pages + the bands it is not a member of, with EVERY write blocked.
   * Sent by the server (snake_case) in the login / /auth/me payloads. */
  is_showcase?: boolean;
  created_at?: string;
  /** Nickname of the linked Team member (team_members.nickname), if any. */
  nickname?: string;
  /** Id of the linked Team member (team_members.id), if the account is
   * linked to a person. `null`/absent = no linked person. Sent by the server
   * (snake_case) in the login / /auth/me payloads. */
  team_member_id?: string | null;
  /** 1 = the server expects this password to be replaced (superadmin "set
   * password" creation, or an admin reset). Drives the forced-change modal. */
  must_change_password?: boolean;
}

export async function apiLogin(username: string, password: string): Promise<AuthUser> {
  const data = await fetchJSON(`${API_BASE}/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  // A fresh session means queued offline mutations can now reach the server:
  // replay them (fire-and-forget; failures keep the queue intact).
  void flushPending();
  return data.user;
}

/** Result of the first login step — either a user (no 2FA) or a pending token. */
export interface LoginStepResult {
  user?: AuthUser;
  twoFactorRequired?: boolean;
  pendingToken?: string;
}

export async function apiLoginStep(username: string, password: string): Promise<LoginStepResult> {
  const data = await fetchJSON(`${API_BASE}/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  return data;
}

export async function apiVerify2fa(pendingToken: string, code: string): Promise<AuthUser> {
  const data = await fetchJSON(`${API_BASE}/auth/2fa/verify`, {
    method: 'POST',
    body: JSON.stringify({ pendingToken, code }),
  });
  void flushPending();
  return data.user;
}

export async function api2faStatus(): Promise<{ enabled: boolean }> {
  return fetchJSON(`${API_BASE}/auth/2fa/status`);
}

export async function api2faSetup(): Promise<{ secret: string; otpauth_url: string; backup_codes: string[] }> {
  return fetchJSON(`${API_BASE}/auth/2fa/setup`, { method: 'POST' });
}

export async function api2faEnable(code: string): Promise<{ enabled: boolean }> {
  return fetchJSON(`${API_BASE}/auth/2fa/enable`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function api2faDisable(code: string): Promise<{ enabled: boolean }> {
  return fetchJSON(`${API_BASE}/auth/2fa/disable`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

// ---- Self-service Team card (the logged-in user's own identity) ----

export async function apiGetMyTeam(): Promise<TeamMemberDTO | null> {
  try {
    return await fetchJSON(`${API_BASE}/me/team`);
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// ---- Personal calendar (SPEC-PERSONAL-CALENDAR, 2026-10-21) -----------------
export interface MyCalendarLink { url: string; webcal_url: string; member_linked?: boolean }
export interface MyCalendarShow extends ShowDTO {
  band_name?: string;
  band_slug?: string;
  my_role?: string;
  my_time?: string;
}

/** The user's own .ics subscription link (created on first read). */
export async function apiGetMyCalendar(): Promise<MyCalendarLink> {
  return fetchJSON(`${API_BASE}/me/calendar`);
}

/** Rotate the token — the OLD link dies (403), a fresh one is returned. */
export async function apiRotateMyCalendar(): Promise<MyCalendarLink> {
  return fetchJSON(`${API_BASE}/me/calendar/rotate`, { method: 'POST' });
}

/** The JSON twin of the feed: the shows where the user is « concerned ». */
export async function apiGetMyCalendarShows(): Promise<{ shows: MyCalendarShow[] }> {
  return fetchJSON(`${API_BASE}/me/calendar/shows`);
}

export async function apiUpdateMyTeam(patch: Partial<TeamMemberDTO>): Promise<TeamMemberDTO> {
  return fetchJSON(`${API_BASE}/me/team`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

// Self-service documents (own Team card): passports / visas / loyalty programs.
const selfDoc = (table: string) => ({
  list: async (): Promise<any[]> => fetchJSON(`${API_BASE}/me/team/${table}`),
  create: async (body: any): Promise<any> => fetchJSON(`${API_BASE}/me/team/${table}`, {
    method: 'POST', body: JSON.stringify(body),
  }),
  update: async (docId: string, body: any): Promise<any> => fetchJSON(`${API_BASE}/me/team/${table}/${docId}`, {
    method: 'PUT', body: JSON.stringify(body),
  }),
  remove: async (docId: string): Promise<any> => fetchJSON(`${API_BASE}/me/team/${table}/${docId}`, {
    method: 'DELETE',
  }),
});

export const apiMyPassports = selfDoc('passports');
export const apiMyVisas = selfDoc('visas');
export const apiMyAirlines = selfDoc('team_member_airlines');

// ---- Crew availability (unavailability declared per band, V1) ----
export interface AvailabilityDTO {
  id: string;
  team_member_id: string;
  band_id: string;
  band_name?: string;
  member_name?: string;
  date_from: string; // ISO yyyy-mm-dd
  date_to: string;   // ISO yyyy-mm-dd
  reason?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AvailabilityConflict {
  member_id: string;
  conflicts: Array<
    | { type: 'unavailable'; id: string; date_from: string; date_to: string; reason?: string }
    | { type: 'double_booked'; other_show_id: string; other_show_name: string; date: string }
  >;
}

// Self-service (own rows, under /api/me).
export async function apiGetMyAvailability(): Promise<{
  bands: { id: string; name: string }[];
  availability: AvailabilityDTO[];
}> {
  return fetchJSON(`${API_BASE}/me/availability`);
}
export async function apiCreateMyAvailability(
  body: { band_id: string; date_from: string; date_to: string; reason?: string },
): Promise<AvailabilityDTO> {
  return fetchJSON(`${API_BASE}/me/availability`, { method: 'POST', body: JSON.stringify(body) });
}
export async function apiUpdateMyAvailability(
  id: string,
  body: { date_from: string; date_to: string; reason?: string },
): Promise<AvailabilityDTO> {
  return fetchJSON(`${API_BASE}/me/availability/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export async function apiDeleteMyAvailability(id: string): Promise<void> {
  await fetchJSON(`${API_BASE}/me/availability/${id}`, { method: 'DELETE' });
}

// Manager (per band).
export async function apiGetBandAvailability(bandId: string): Promise<AvailabilityDTO[]> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/availability`);
}
export async function apiCreateBandAvailability(
  bandId: string,
  body: { team_member_id: string; date_from: string; date_to: string; reason?: string },
): Promise<AvailabilityDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/availability`, { method: 'POST', body: JSON.stringify(body) });
}
export async function apiUpdateBandAvailability(
  bandId: string,
  id: string,
  body: { team_member_id: string; date_from: string; date_to: string; reason?: string },
): Promise<AvailabilityDTO> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/availability/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export async function apiDeleteBandAvailability(bandId: string, id: string): Promise<void> {
  await fetchJSON(`${API_BASE}/bands/${bandId}/availability/${id}`, { method: 'DELETE' });
}

// Conflict warning for a show assignment (viewer+ on the show).
export async function apiGetShowAvailabilityConflicts(
  showId: string,
  memberIds: string[],
): Promise<AvailabilityConflict[]> {
  const qs = new URLSearchParams({ members: memberIds.join(',') });
  return fetchJSON(`${API_BASE}/shows/${showId}/availability-conflicts?${qs}`);
}

/* ---- Crew Planning V2: read-only load view + who-is-free (viewer+) ---- */

/** A member's blocked period (from V1 `availability`) overlapping the window. */
export interface AvailabilityBlock {
  date_from: string; // yyyy-mm-dd
  date_to: string;   // yyyy-mm-dd
  reason?: string;
}

/** A band show inside the window and whether this member is selected on it. */
export interface AvailabilityMemberShow {
  show_id: string;
  date: string;      // yyyy-mm-dd
  name: string;
  on_show: boolean;
}

/** One active member's load over the window (GET /availability/load). */
export interface AvailabilityLoadMember {
  team_member_id: string;
  name: string;
  category: 'musician' | 'crew' | string;
  roles: string[];
  unavailable: AvailabilityBlock[];
  shows: AvailabilityMemberShow[];
}

/** One member who is free on the queried date (GET /availability/who-is-free). */
export interface WhoIsFreeMember {
  team_member_id: string;
  name: string;
  category: 'musician' | 'crew' | string;
  roles: string[];
  already_on_show: boolean;
  reason?: string | null;
}

export type WhoIsFreeCategory = 'all' | 'musician' | 'crew';

/**
 * Per-person load view over a window (default: today → +60 days). Members
 * returned = active members of the band. Read-only (viewer+).
 */
export async function apiGetBandAvailabilityLoad(
  bandId: string,
  opts?: { from?: string; to?: string },
): Promise<AvailabilityLoadMember[]> {
  const qs = new URLSearchParams();
  if (opts?.from) qs.set('from', opts.from);
  if (opts?.to) qs.set('to', opts.to);
  const query = qs.toString();
  return fetchJSON(`${API_BASE}/bands/${bandId}/availability/load${query ? `?${query}` : ''}`);
}

/**
 * Members of the band with NO unavailability covering `date`, optionally
 * filtered by category (musician/crew/all). `already_on_show` flags members
 * already selected on a band show that day (potential double-book).
 */
export async function apiWhoIsFree(
  bandId: string,
  opts: { date: string; category?: WhoIsFreeCategory; roles?: string[] },
): Promise<WhoIsFreeMember[]> {
  const qs = new URLSearchParams({ date: opts.date });
  if (opts.category) qs.set('category', opts.category);
  if (opts.roles && opts.roles.length) qs.set('roles', opts.roles.join(','));
  return fetchJSON(`${API_BASE}/bands/${bandId}/availability/who-is-free?${qs.toString()}`);
}


export async function apiLogout(): Promise<void> {
  await fetchJSON(`${API_BASE}/auth/logout`, { method: 'POST' });
}

export async function apiMe(): Promise<AuthUser | null> {
  try {
    const data = await fetchJSON(`${API_BASE}/auth/me`);
    return data.user;
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

/** UI card-order preferences (cards drag & drop, 2026-10-24): per-user, per-page. */
export async function apiGetUiPrefs(pageId: string): Promise<{ order: string[]; open?: string[] }> {
  return fetchJSON(`${API_BASE}/auth/ui-prefs/${encodeURIComponent(pageId)}`);
}
export async function apiPutUiPrefs(pageId: string, prefs: { order: string[]; open?: string[] }): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/auth/ui-prefs/${encodeURIComponent(pageId)}`, {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

export async function apiGetUsers(): Promise<AuthUser[]> {
  return fetchJSON(`${API_BASE}/users`);
}

export async function apiCreateUser(username: string, password: string, role: AuthUser['role']) {
  return fetchJSON(`${API_BASE}/users`, {
    method: 'POST',
    body: JSON.stringify({ username, password, role }),
  });
}

export async function apiUpdateUser(id: number, data: { password?: string; role?: AuthUser['role'] }) {
  return fetchJSON(`${API_BASE}/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function apiDeleteUser(id: number) {
  return fetchJSON(`${API_BASE}/users/${id}`, { method: 'DELETE' });
}

// ---- Riders (show-specific tech riders) ----
export interface CreateRiderPayload {
  bandId: string;
  showId: string;
  transport?: 'van' | 'plane' | 'tourbus' | 'train';
  activeMusicianIds: string[];
  activeCrewIds: string[];
  name?: string;
  backlineProviders?: Record<string, 'band' | 'venue'>;
}

export async function apiGetRiders(params?: { bandId?: string; showId?: string }): Promise<any> {
  const qs = new URLSearchParams();
  if (params?.bandId) qs.set('bandId', params.bandId);
  if (params?.showId) qs.set('showId', params.showId);
  const url = qs.toString() ? `${API_BASE}/riders?${qs}` : `${API_BASE}/riders`;
  return fetchJSON(url);
}

export async function apiGetRiderById(id: string): Promise<any> {
  return fetchJSON(`${API_BASE}/riders/${id}`);
}

export async function apiCreateRider(payload: CreateRiderPayload): Promise<any> {
  return fetchJSON(`${API_BASE}/riders`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function apiUpdateRider(
  id: string,
  payload: Partial<{
    transport: 'van' | 'plane' | 'tourbus' | 'train';
    activeMusicianIds: string[];
    activeCrewIds: string[];
    requirements: Record<string, unknown>;
    stagePlotId: string | null;
    name: string;
  }>,
): Promise<any> {
  return fetchJSON(`${API_BASE}/riders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function apiDeleteRider(id: string): Promise<void> {
  return fetchJSON(`${API_BASE}/riders/${id}`, { method: 'DELETE' });
}

/** Rebuild the show rider's requirements from the band's CURRENT config and the
    rider's CURRENT selection/transport (backline provider overrides preserved).
    Used by the "Update musicians & crew" button after changing the people. */
export async function apiRegenerateRider(id: string): Promise<any> {
  return fetchJSON(`${API_BASE}/riders/${id}/regenerate`, { method: 'POST' });
}

// Per-show member roles: { memberId: string[] }. Overrides the band-wide tags
// for THIS show's rider. Members absent from the map = band default tags.
export async function apiGetRiderRoles(id: string): Promise<Record<string, string[]>> {
  return fetchJSON(`${API_BASE}/riders/${id}/roles`);
}

// Replace the FULL role list for one member on this show. Empty = band default.
export async function apiSetRiderRoles(id: string, memberId: string, roles: string[]): Promise<{ memberId: string; roles: string[] }> {
  return fetchJSON(`${API_BASE}/riders/${id}/roles/${memberId}`, {
    method: 'PUT',
    body: JSON.stringify({ roles }),
  });
}

// ---- Team (people registry) ----
// A person exists ONCE in `team_members`, keyed by a TEXT uuid SHARED with the
// rider musicians/crew. Saving the rider auto-creates/updates the matching
// person server-side (see server/routes/rider.js syncPeopleFromRider), so this
// API is for the identity/document side only — never for stage/technical data.
export type TeamRole =
  | 'musician' | 'manager' | 'tour manager' | 'foh' | 'mon' | 'lights' | 'crew' | 'other';

// Person's global category (what they ARE) — distinct from per-band roles.
// Kept as the first/primary tag mirror; full multi-tag set lives in `tags`.
export type TeamCategory =
  | 'musician' | 'crew' | 'tech' | 'manager'
  | 'tour manager' | 'lights' | 'foh' | 'mon' | 'driver' | 'other';

export interface PassportDTO {
  id: string;
  team_member_id: string;
  passport_number: string;
  nationality?: string;
  expiry_date?: string;
  issue_date?: string;
  issuing_country?: string;
  scan_path?: string;
  /** True when a scan exists but is NOT readable by this caller (the reference
   *  itself is withheld). Lets the UI say « restricted » instead of « none ». */
  has_scan?: boolean;
  notes?: string;
}

export interface VisaDTO {
  id: string;
  team_member_id: string;
  country: string;
  visa_type?: string;
  visa_number?: string;
  expiry_date?: string;
  entry_type?: 'single' | 'multiple';
  scan_path?: string;
  has_scan?: boolean;
  notes?: string;
}

// One frequent-flyer / loyalty program per airline. A person can hold SEVERAL.
export interface AirlineDTO {
  id: string;
  team_member_id: string;
  company: string;
  program_number?: string;
  tier?: string;
  alliance?: string;
  notes?: string;
}

export interface TeamMemberDTO {
  id: string;
  name: string;
  slug?: string;
  category: TeamCategory;
  role?: string;
  tags?: string[];
  /** Base role propagated to band rider cards at link time:
   * musician → main/session/guest ; crew → main/session (2026-08-09). */
  base_role?: string;
  email?: string;
  phone?: string;
  notes?: string;
  photo_url?: string;
  nickname?: string;
  address?: string;
  date_of_birth?: string;
  frequent_flyer?: string;
  /** Aéroport de préférence (2026-10-17) : override celui du groupe, origine des vols du membre. */
  default_airport?: string;
  airline_program?: string;
  loyalty_program?: string;
  nationality?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  // Identity consolidated from the rider cards into the Team registry.
  real_name?: string;
  last_name?: string;
  post_code?: string;
  locality?: string;
  country?: string;
  diet?: string;
  diet_notes?: string;
  non_food_allergies?: string;
  passports?: PassportDTO[];
  visas?: VisaDTO[];
  airlines?: AirlineDTO[];
  /** Server marker (2026-10-07): this is a COLLEAGUE's card whose personal data
   *  was withheld (identity, contacts, health, documents). The edit page then
   *  freezes those fields and hides the document sections. */
  restricted?: boolean;
  /** Per-band TRUTH of what exists: the rider card kind the person actually
   *  holds in that band's rider (from `rider_musicians` / `rider_crew`), NOT the
   *  legacy `link_role` mirror. Null = linked but no card minted yet. A person
   *  may be `crew` in one band and `musician` in another (2026-10-12). */
  bands?: {
    id: string; name: string; slug: string; link_role?: string; roles?: string[];
    card_kind?: 'musician' | 'crew' | null;
  }[];
  /** Result of a link/move mutation (present only on those responses). */
  card?: { created: boolean; kind: 'musician' | 'crew'; conflict: boolean };
  move?: { moved: boolean; kind: 'musician' | 'crew'; reason?: 'already' | 'not_found' };
  /** Per-band manual ordering (team_member_bands.order). Null = not manually
   * ordered → the page falls back to the automatic category/baseRole/name
   * sort. (2026-09-02) */
  order?: number;
}

export interface ExpiringDoc {
  kind: 'passport' | 'visa';
  member_name: string;
  expiry_date: string;
  country?: string;
  passport_number?: string;
  visa_type?: string;
}

export async function apiGetTeam(params?: { band?: string; role?: TeamRole; category?: TeamCategory; tag?: string; tags?: string[] }): Promise<TeamMemberDTO[]> {
  const q = new URLSearchParams();
  if (params?.band) q.set('band', params.band);
  if (params?.category) q.set('category', params.category);
  if (params?.role) q.set('role', params.role);
  if (params?.tag) q.set('tag', params.tag);
  if (params?.tags && params.tags.length > 0) q.set('tags', params.tags.join(','));
  const qs = q.toString();
  return fetchJSON(`${API_BASE}/team${qs ? `?${qs}` : ''}`);
}
export async function apiGetTeamMember(id: string): Promise<TeamMemberDTO> {
  return fetchJSON(`${API_BASE}/team/${id}`);
}
export async function apiCreateTeamMember(data: Partial<TeamMemberDTO> & { band_id?: string }): Promise<TeamMemberDTO> {
  return fetchJSON(`${API_BASE}/team`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateTeamMember(id: string, data: Partial<TeamMemberDTO>): Promise<TeamMemberDTO> {
  return fetchJSON(`${API_BASE}/team/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteTeamMember(id: string): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/team/${id}`, { method: 'DELETE' });
}
export async function apiGetExpiringDocs(days = 180): Promise<{ days: number; expired: ExpiringDoc[]; expiring: ExpiringDoc[] }> {
  return fetchJSON(`${API_BASE}/team/expiring?days=${days}`);
}
// Same human present in two bands' riders = two rows (each rider minted its own
// uuid). Merging keeps ONE identity and records the retired uuid as an alias so
// the next rider save does not recreate the duplicate.
//
// Detection is by STRONG KEYS since v0.721 (2026-10-12): a group carries the
// matched `keys`, a human-readable `evidence` list, and a `confidence`
// (`strong` = a real key matched, `weak` = a name-only hint).
export interface TeamDuplicateGroup {
  members: TeamMemberDTO[];
  confidence: 'strong' | 'weak';
  keys: string[];
  evidence: string[];
}
/* ---------------------------------------------------------------- Activity -- *
 * Deletion journal + Undo (LOT B phase 1, 2026-10-15).
 *   GET  /api/activity?band=<id>&entity_id=<id>&limit=<n>  → one row per Undo button
 *   POST /api/activity/:id/undo                            → recreate from the snapshot
 * PHASE 1 = DELETIONS only: undoing a FIELD EDIT is phase 2 and is not offered.
 * The server already applies the tenant boundary (rows of bands the caller does
 * not manage are filtered out), so this is a read, not an authority check.
 * ---------------------------------------------------------------------------- */

export interface ActivityRestoreCounts {
  [table: string]: number;
}

export interface ActivityActionDTO {
  id: number;
  /** e.g. 'team_member.delete' | 'team_member_band.delete' | 'show.edit' */
  action: string;
  entity: string;
  entity_id: string;
  /** null = an action that spanned several groups (a person deletion) or a
   *  band-less one (a global venue, a personal document). */
  band_id: string | null;
  bands: { id: string; name: string }[];
  /** Nickname when set, otherwise the name — what activity.undoDone interpolates. */
  name: string;
  user_id: number | null;
  created_at: string;
  restores: ActivityRestoreCounts;
  /** 'delete' = a destruction (LOT B/C); 'edit' = a field edit (LOT D). */
  kind?: 'delete' | 'edit';
  /** For an edit row: the columns the edit changed (verbatim keys). */
  columns?: string[];
  undo: { available: boolean; conflict: boolean; reason: string | null };
}

export interface ActivityUndoResult {
  /** 'restored' | 'already_exists' | 'nothing' | 'conflict' | 'not_found' */
  status: string;
  changed: boolean;
  /** true when nothing was written because the row changed since (an EDIT). */
  conflict?: boolean;
  reason?: string | null;
  created?: ActivityRestoreCounts;
  skipped?: ActivityRestoreCounts;
  entry?: ActivityActionDTO;
}

export async function apiGetActivity(params?: {
  band?: string;
  entityId?: string;
  limit?: number;
}): Promise<{ actions: ActivityActionDTO[]; count: number }> {
  const q = new URLSearchParams();
  if (params?.band) q.set('band', params.band);
  if (params?.entityId) q.set('entity_id', params.entityId);
  if (params?.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return fetchJSON(`${API_BASE}/activity${qs ? `?${qs}` : ''}`);
}

export async function apiUndoActivity(id: number): Promise<ActivityUndoResult> {
  return fetchJSON(`${API_BASE}/activity/${id}/undo`, { method: 'POST' });
}

export async function apiGetTeamDuplicates(): Promise<TeamDuplicateGroup[]> {
  return fetchJSON(`${API_BASE}/team/duplicates`);
}
// Pairs previously declared "two different people" — so the UI can reopen one.
export async function apiGetTeamNotDuplicates(): Promise<{ members: TeamMemberDTO[] }[]> {
  return fetchJSON(`${API_BASE}/team/not-duplicates`);
}
export async function apiUndoTeamNotDuplicate(canonicalId: string, otherId: string): Promise<{ ok: boolean; removed: boolean }> {
  return fetchJSON(`${API_BASE}/team/${canonicalId}/not-duplicate/${otherId}`, { method: 'DELETE' });
}
export async function apiMergeTeamMembers(canonicalId: string, duplicateId: string): Promise<{ merged: boolean; canonical: TeamMemberDTO }> {
  return fetchJSON(`${API_BASE}/team/${canonicalId}/merge`, {
    method: 'POST', body: JSON.stringify({ duplicate_id: duplicateId }),
  });
}
export async function apiMarkTeamNotDuplicate(canonicalId: string, duplicateId: string): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/team/${canonicalId}/not-duplicate`, {
    method: 'POST', body: JSON.stringify({ duplicate_id: duplicateId }),
  });
}
// Manual reorder of a band's Team page (drag to reorder, like Musicians/Crew).
// `bandId` may be an id or slug. Per-band order lives on team_member_bands.
export async function apiReorderTeam(bandId: string, orderedIds: string[]): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/team/reorder`, {
    method: 'PUT', body: JSON.stringify({ bandId, orderedIds }),
  });
}
export async function apiLinkTeamMemberBand(id: string, band_id: string, role?: string): Promise<TeamMemberDTO> {
  return fetchJSON(`${API_BASE}/team/${id}/bands`, { method: 'POST', body: JSON.stringify({ band_id, role }) });
}
export async function apiUnlinkTeamMemberBand(id: string, bandId: string): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/team/${id}/bands/${bandId}`, { method: 'DELETE' });
}
// Move a rider card between this band's two lists (musician ↔ crew). The card
// keeps every common field; the old list loses it in the same server operation.
export async function apiMoveRiderCardKind(id: string, bandId: string, kind: 'musician' | 'crew'): Promise<TeamMemberDTO> {
  return fetchJSON(`${API_BASE}/team/${id}/bands/${bandId}/card-kind`, { method: 'POST', body: JSON.stringify({ kind }) });
}

// Passports & visas share the same CRUD shape.
export async function apiCreatePassport(memberId: string, data: Partial<PassportDTO>): Promise<PassportDTO> {
  return fetchJSON(`${API_BASE}/team/${memberId}/passports`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdatePassport(memberId: string, docId: string, data: Partial<PassportDTO>): Promise<PassportDTO> {
  return fetchJSON(`${API_BASE}/team/${memberId}/passports/${docId}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeletePassport(memberId: string, docId: string): Promise<void> {
  return fetchJSON(`${API_BASE}/team/${memberId}/passports/${docId}`, { method: 'DELETE' });
}
// Passport MRZ auto-fill (option B, pivot confidentialité): the OCR happens
// ENTIRELY in the browser (src/lib/mrzOcr.ts, tesseract.js) — the image never
// leaves the machine. Only the two raw MRZ LINES (text) are sent to our
// server, which validates them with local ICAO check digits. Returns a
// PROPOSAL — nothing is written server-side.
export interface PassportOcrResult {
  ok: boolean;
  method?: 'mrz';
  proposal?: Partial<PassportDTO>;
  // issue_date is an ESTIMATE (not in the MRZ): expiry − 10 y, or − 5 y if
  // the holder was under ~15 at issue (DOB). Always labeled "estimated" in
  // the UI, distinct from the MRZ extraction.
  estimated?: string[];
  extra?: { surname?: string; given_names?: string; date_of_birth?: string; sex?: string };
  error?: string;
}
export async function apiValidateMrz(
  memberId: string, docId: string, mrzLine1: string, mrzLine2: string,
  target: DocScanTarget = {},
): Promise<PassportOcrResult> {
  // `target.self` → self-service route (Account → Documents): the caller's OWN
  // passport, resolved from the session (no memberId), same guards as the scan.
  const url = target.self
    ? `${API_BASE}/me/team/documents/passport/${docId}/validate-mrz`
    : `${API_BASE}/team/${memberId}/documents/passport/${docId}/validate-mrz`;
  return fetchJSON(url, {
    method: 'POST',
    body: JSON.stringify({ mrz_line1: mrzLine1, mrz_line2: mrzLine2 }),
  });
}
export async function apiCreateVisa(memberId: string, data: Partial<VisaDTO>): Promise<VisaDTO> {
  return fetchJSON(`${API_BASE}/team/${memberId}/visas`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateVisa(memberId: string, docId: string, data: Partial<VisaDTO>): Promise<VisaDTO> {
  return fetchJSON(`${API_BASE}/team/${memberId}/visas/${docId}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteVisa(memberId: string, docId: string): Promise<void> {
  return fetchJSON(`${API_BASE}/team/${memberId}/visas/${docId}`, { method: 'DELETE' });
}

// Definitive duplicate dismissal (SPEC-TEAM-ACCOUNT-REORDER): « These are
// 2 different people » — the pair is never re-proposed by the detector
// (global, persistent, no reopen). Any member with access to BOTH people.
export async function apiDismissDuplicate(memberA: string, memberB: string): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/team/duplicates/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ member_a: memberA, member_b: memberB }),
  });
}

// Frequent-flyer / loyalty programs (same CRUD shape as passports & visas).
export async function apiCreateAirline(memberId: string, data: Partial<AirlineDTO>): Promise<AirlineDTO> {
  return fetchJSON(`${API_BASE}/team/${memberId}/team_member_airlines`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateAirline(memberId: string, docId: string, data: Partial<AirlineDTO>): Promise<AirlineDTO> {
  return fetchJSON(`${API_BASE}/team/${memberId}/team_member_airlines/${docId}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteAirline(memberId: string, docId: string): Promise<void> {
  return fetchJSON(`${API_BASE}/team/${memberId}/team_member_airlines/${docId}`, { method: 'DELETE' });
}

// ---- Finance (per show) ----
// The deal itself (fee, deposit, payment status, per diem) lives on the show
// row — see the finance fields on ShowDTO. Expenses are a child collection.
export type ExpenseCategory =
  | 'travel' | 'accommodation' | 'catering' | 'backline' | 'production'
  | 'crew' | 'per diem' | 'commission' | 'tax' | 'other';

export const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'travel', 'accommodation', 'catering', 'backline', 'production',
  'crew', 'per diem', 'commission', 'tax', 'other',
];

// No automatic FX conversion: each amount keeps the currency it was agreed in.
export const CURRENCIES = ['EUR', 'CHF', 'USD', 'GBP', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'KZT'];

export interface ExpenseDTO {
  id: number;
  tour_date_id: number;
  category: ExpenseCategory;
  label?: string;
  amount: number;
  currency?: string;
  paid?: number;
  payee?: string;
  notes?: string;
}

export async function apiGetExpenses(showId: string): Promise<ExpenseDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/expenses`);
}
export async function apiCreateExpense(showId: string, data: Partial<ExpenseDTO>): Promise<ExpenseDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/expenses`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateExpense(showId: string, id: string, data: Partial<ExpenseDTO>): Promise<ExpenseDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteExpense(showId: string, id: string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/expenses/${id}`, { method: 'DELETE' });
}

// ---- Musician fees / cachets (one structured fee per member × show) ----
// A fee is financial data: every route is gated by the `finance.fees` (or the
// `finance.expenses`) capability server-side (reads AND writes). band_id is
// derived server-side from the show — never sent by the client. Amounts are
// NEVER converted between currencies.
export type FeeStatus = 'due' | 'paid' | 'partial' | 'cancelled';
export const FEE_STATUSES: FeeStatus[] = ['due', 'paid', 'partial', 'cancelled'];

export interface ShowFeeDTO {
  id: number;
  tour_date_id: number;
  team_member_id: string;
  member_name?: string;
  amount: number;
  currency: string;
  status: FeeStatus;
  paid_amount: number;
  paid_date?: string;
  role_note?: string;
  notes?: string;
}

/** Writable fee fields — team_member_id is required on create/bulk only. */
export interface FeeInput {
  team_member_id?: string;
  amount?: number;
  currency?: string;
  status?: FeeStatus;
  paid_amount?: number;
  paid_date?: string;
  role_note?: string;
  notes?: string;
}

export interface FeesFilter {
  from?: string;
  to?: string;
  tour_id?: string;
}

export interface FeesSummaryRow {
  team_member_id: string;
  member_name: string;
  /** Raw sum across currencies (NO conversion) — use by_currency for the truth. */
  total_due: number;
  total_paid: number;
  gigs: number;
  by_currency: { currency: string; due: number; paid: number }[];
}

export interface MemberInvoiceLine {
  id: number;
  tour_date_id: number;
  date?: string;
  show_name?: string;
  tour_id?: string | null;
  tour_name?: string | null;
  venue_name?: string | null;
  city?: string | null;
  country?: string | null;
  amount: number;
  currency: string;
  status: FeeStatus;
  paid_amount?: number;
  paid_date?: string;
  role_note?: string;
  notes?: string;
}

export interface MemberInvoice {
  band: { id: string; name: string; slug?: string };
  member: { id: string; name: string; role?: string; category?: string; email?: string };
  period: { from: string; to: string; tour_id: string };
  lines: MemberInvoiceLine[];
  totals: { currency: string; due: number; paid: number; gigs: number }[];
  note: string;
}

export async function apiGetShowFees(showId: string): Promise<ShowFeeDTO[]> {
  return fetchJSON(`${API_BASE}/shows/${showId}/fees`);
}
export async function apiCreateShowFee(showId: string, data: FeeInput): Promise<ShowFeeDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/fees`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateShowFee(showId: string, feeId: number | string, data: FeeInput): Promise<ShowFeeDTO> {
  return fetchJSON(`${API_BASE}/shows/${showId}/fees/${feeId}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteShowFee(showId: string, feeId: number | string): Promise<void> {
  return fetchJSON(`${API_BASE}/shows/${showId}/fees/${feeId}`, { method: 'DELETE' });
}
/** Apply the SAME fee to several members ({team_member_ids}) or every active
 * member on the show ({all_on_show:true}). Idempotent upsert. */
export async function apiBulkShowFees(
  showId: string,
  data: FeeInput & { team_member_ids?: string[]; all_on_show?: boolean },
): Promise<{ applied: number; fees: ShowFeeDTO[] }> {
  return fetchJSON(`${API_BASE}/shows/${showId}/fees/bulk`, { method: 'POST', body: JSON.stringify(data) });
}

function feesQuery(filter?: FeesFilter): string {
  const q = new URLSearchParams();
  if (filter?.from) q.set('from', filter.from);
  if (filter?.to) q.set('to', filter.to);
  if (filter?.tour_id) q.set('tour_id', filter.tour_id);
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** Per-member payout aggregate for a band (period / tour optional). */
export async function apiGetFeesSummary(bandId: string, filter?: FeesFilter): Promise<FeesSummaryRow[]> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/fees/summary${feesQuery(filter)}`);
}

/** Structured recap-invoice data for one member (the frontend renders the PDF). */
export async function apiGetMemberInvoiceData(bandId: string, memberId: string, filter?: FeesFilter): Promise<MemberInvoice> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/fees/member/${encodeURIComponent(memberId)}/invoice${feesQuery(filter)}`);
}

// ---- User management (multi-tenant) ----
// A user only reaches the bands listed in `bands` below. `global_role`
// 'superadmin' runs the platform; everyone else is scoped by band.
export type GlobalRole = 'superadmin' | 'user';
/**
 * The role of a user ON A BAND. `tech` is the current name (renamed from `crew`,
 * 2026-10-08); `crew` stays in the union because the DB CHECK still ACCEPTS it on
 * purpose (a row not yet migrated, or a rollback, must never violate the
 * constraint) — a payload carrying `crew` is therefore legal and the client must
 * not be narrower than the server.
 */
export type BandRole = 'owner' | 'editor' | 'tech' | 'crew' | 'viewer';

export interface UserBandLink {
  id: string;
  name: string;
  slug: string;
  role: BandRole;
  finance_access?: number;
  layout_access?: number;
  /** Capabilities granted to this user on that band (chantier C, 2026-09-25). */
  permissions?: string[];
  /** Read DENIES (`<family>.view` stored granted = 0) — the « — » level of the
      page×level grid (2026-10-08). Distinct from an ABSENT grant: absence keeps
      the historical behaviour (reads allowed). */
  denied?: string[];
}

export interface AppUser {
  id: number;
  username: string;
  email?: string;
  display_name?: string;
  role: string;               // legacy flat role, kept for compatibility
  global_role: GlobalRole;
  is_active: boolean;
  must_change_password?: boolean;
  last_login_at?: string;
  created_at?: string;
  team_member_id?: string | null;
  has_2fa?: boolean;
  login_locked?: boolean;
  login_failures?: number;
  bands: UserBandLink[];
}

export interface BandMember {
  id: number;
  username: string;
  email?: string;
  display_name?: string;
  is_active: number;
  role: BandRole;
  finance_access?: number;
  /** Owner-granted right to edit this band's PDF layouts (0/1). (2026-09-24)
      LEGACY derived value: the capability matrix below is authoritative. */
  layout_access?: number;
  /** The capabilities granted to THIS member on this band — the complete set by
      construction (chantier C, 2026-09-25). Absent on a pre-matrix payload. */
  permissions?: string[];
  /** Read DENIES (`<family>.view` stored granted = 0) — the « — » level of the
      page×level grid (2026-10-08). Distinct from an ABSENT grant: absence keeps
      the historical behaviour (reads allowed). */
  denied?: string[];
}

export interface InvitationDTO {
  id: string;
  email: string;
  band_id?: string | null;
  band_name?: string | null;
  band_role: BandRole;
  global_role: GlobalRole;
  expires_at: number;
  created_at: string;
  /** Full re-viewable invite link (persisted token_raw, superadmin-only route).
   *  null when the invitation predates the token_raw column (no stored link). */
  invite_link?: string | null;
}

export interface AuditEntry {
  id: number;
  username: string;
  action: string;
  target_type: string;
  target_id: string;
  detail: string;
  created_at: string;
}

export async function apiGetAppUsers(): Promise<AppUser[]> {
  return fetchJSON(`${API_BASE}/users`);
}
export async function apiCreateAppUser(data: {
  username: string; password: string; email?: string;
  display_name?: string; global_role?: GlobalRole;
}): Promise<AppUser> {
  return fetchJSON(`${API_BASE}/users`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateAppUser(id: number, data: {
  username?: string; password?: string; email?: string; display_name?: string;
  global_role?: GlobalRole; is_active?: boolean; team_member_id?: string | null;
}): Promise<AppUser> {
  return fetchJSON(`${API_BASE}/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteAppUser(id: number): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/users/${id}`, { method: 'DELETE' });
}
// Phase alpha (2026-10-20): give a Team member WITHOUT an account a password
// directly — the server CREATES the account (auto username from the person's
// name, team_member_id linked, must_change_password=1 unless opted out).
// 409 if the person already has an account (use the existing reset instead).
export async function apiSetMemberPassword(data: {
  team_member_id: string; password: string; must_change_password?: boolean;
}): Promise<AppUser> {
  return fetchJSON(`${API_BASE}/users/set-password`, { method: 'POST', body: JSON.stringify(data) });
}
// Sign a user out of every device immediately (superadmin).
export async function apiRevokeUserSessions(id: number): Promise<{ revoked: boolean }> {
  return fetchJSON(`${API_BASE}/users/${id}/revoke-sessions`, { method: 'POST' });
}
// Manually clear an account's failed-login lockout (admin/superadmin).
export async function apiUnlockUser(id: number): Promise<{ unlocked: boolean; username: string }> {
  return fetchJSON(`${API_BASE}/users/${id}/unlock`, { method: 'POST' });
}
// The WHOLE Team registry (team_members) — the admin console shows every person
// (most have no login) and can invite them. `has_account` is computed SERVER-SIDE
// (a users row carrying `team_member_id = <this id>`), so the client never fires
// an N+1 per person. `email` is the person's Team-card email on this
// superadmin-only route.
export interface PersonBandLink { id: string; name: string; slug?: string; }
export interface PersonBrief {
  id: string;
  name: string;
  nickname?: string;
  category?: string;
  email?: string;
  bands?: PersonBandLink[];
  has_account?: boolean;
  account?: { id: number; username: string } | null;
}
export async function apiGetAllPeople(): Promise<PersonBrief[]> {
  return fetchJSON(`${API_BASE}/users/people`);
}

// ---- Band membership ----
export async function apiGetBandMembers(bandId: string): Promise<BandMember[]> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/members`);
}
export async function apiSetBandMember(
  bandId: string,
  userId: number,
  role: BandRole,
  financeAccess?: number | boolean,
  layoutAccess?: number | boolean,
  /**
   * The COMPLETE set of capabilities granted to this member (chantier C,
   * 2026-09-25). When provided it is authoritative and the two legacy booleans
   * are NOT sent (the server replaces the whole matrix: rows absent from the
   * array are deleted). An unknown or non-delegable name is refused by the
   * server with a 400 — never an implicit escalation.
   */
  permissions?: string[],
  /**
   * The EXPLICIT READ DENIES of this member (`<family>.view` markers stored with
   * granted = 0) — the « — » level of the page×level grid (2026-10-08). Only the
   * deny markers are accepted by the server: a capability name here is a 400, so
   * this field can never grant anything.
   */
  denied?: string[],
): Promise<unknown> {
  const body: Record<string, unknown> = { role };
  if (Array.isArray(permissions)) {
    body.permissions = permissions;
    if (Array.isArray(denied)) body.denied = denied;
    return fetchJSON(`${API_BASE}/bands/${bandId}/members/${userId}`, {
      method: 'PUT', body: JSON.stringify(body),
    });
  }
  if (Array.isArray(denied)) body.denied = denied;
  if (financeAccess !== undefined) body.finance_access = financeAccess ? 1 : 0;
  // Same convention as finance_access: an ABSENT field leaves the stored value
  // untouched, anything else is normalised to 0/1. (2026-09-24)
  if (layoutAccess !== undefined) body.layout_access = layoutAccess ? 1 : 0;
  return fetchJSON(`${API_BASE}/bands/${bandId}/members/${userId}`, {
    method: 'PUT', body: JSON.stringify(body),
  });
}
export async function apiRemoveBandMember(bandId: string, userId: number): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/bands/${bandId}/members/${userId}`, { method: 'DELETE' });
}

// ---- Invitations ----
// The raw token is returned ONCE, at creation. Only its hash is stored server
// side, so the link cannot be recovered later — copy it immediately.
export interface InvitationDelivery {
  sent: boolean;
  mode: string;
  error?: string | null;
}
export interface InvitationCreateResult {
  id: string;
  email: string;
  invite_path: string;
  invite_link: string;
  expires_in_days: number;
  delivery: InvitationDelivery;
}
export async function apiCreateInvitation(data: {
  email: string; band_id?: string; band_role?: BandRole;
  global_role?: GlobalRole; expires_days?: number;
}): Promise<InvitationCreateResult> {
  return fetchJSON(`${API_BASE}/invitations`, { method: 'POST', body: JSON.stringify(data) });
}
export async function apiGetInvitations(): Promise<InvitationDTO[]> {
  return fetchJSON(`${API_BASE}/invitations`);
}
export async function apiRevokeInvitation(id: string): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/invitations/${id}`, { method: 'DELETE' });
}
/** Re-emit the SAME invitation email (same link, recovered from the stored
 *  token_raw — NOT a new token). Superadmin only. Returns the delivery summary. */
export async function apiResendInvitation(id: string): Promise<InvitationCreateResult> {
  return fetchJSON(`${API_BASE}/invitations/${id}/resend`, { method: 'POST' });
}
export async function apiGetInvitation(token: string): Promise<{ email: string; band: string | null; band_role: BandRole }> {
  return fetchJSON(`${API_BASE}/auth/invitation/${token}`);
}
export async function apiAcceptInvitation(token: string, username: string, password: string): Promise<{ user: AppUser }> {
  return fetchJSON(`${API_BASE}/auth/accept-invitation`, {
    method: 'POST', body: JSON.stringify({ token, username, password }),
  });
}

// ---- Self-service ----
export async function apiChangePassword(current_password: string, new_password: string): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/auth/change-password`, {
    method: 'POST', body: JSON.stringify({ current_password, new_password }),
  });
}
export async function apiForgotPassword(username: string): Promise<{ ok: boolean; reset_token?: string }> {
  return fetchJSON(`${API_BASE}/auth/forgot-password`, {
    method: 'POST', body: JSON.stringify({ username }),
  });
}
export async function apiResetPassword(token: string, new_password: string): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/auth/reset-password`, {
    method: 'POST', body: JSON.stringify({ token, new_password }),
  });
}

/* ---------------------------------------------------------------- *
 * Public self-service sign-up + e-mail verification + trial (L3)
 *
 * The server answers every failure with `{ error: '<code>', message:
 * '<english text>' }` (ApiError.body). The UI maps the CODE to a localized
 * string and falls back to the server's `message` for an unknown code — hence
 * the two small helpers below.
 * ---------------------------------------------------------------- */

/** Frozen error code of an API failure, or null when the body has none. */
export function apiErrorCode(err: unknown): string | null {
  const body = (err as { body?: unknown } | null)?.body;
  if (body && typeof body === 'object') {
    const code = (body as { error?: unknown }).error;
    if (typeof code === 'string' && code) return code;
  }
  return null;
}

/** English text sent by the server (fallback when the code is unknown). */
export function apiErrorMessage(err: unknown): string {
  const body = (err as { body?: unknown } | null)?.body;
  if (body && typeof body === 'object') {
    const msg = (body as { message?: unknown }).message;
    if (typeof msg === 'string' && msg) return msg;
  }
  if (typeof body === 'string' && body) return body;
  return err instanceof Error ? err.message : '';
}

export interface SignupPayload {
  email: string;
  username: string;
  password: string;
  display_name?: string;
  /** Honeypot: always empty for a human (the field is off-screen + aria-hidden
   *  + no tab stop). A bot that autofills every input trips it. */
  website?: string;
  /** Milliseconds spent on the form (measured at mount). The server rejects
   *  submissions faster than a human could fill the fields. */
  elapsed_ms?: number;
}

/** Public runtime config (no auth). Used to hide entry points the server would
 * refuse anyway — see server/lib/features.js. Fails OPEN (returns the current
 * default) so a network hiccup never hides a working feature; the server
 * remains the authority and answers 403 `signup_disabled` if it is closed. */
export async function apiGetPublicConfig(): Promise<{ signupEnabled: boolean }> {
  try {
    return await fetchJSON(`${API_BASE}/public/config`);
  } catch {
    return { signupEnabled: true };
  }
}

/** Self-service sign-up. NO session is created: the account has to verify its
 * e-mail address first (the server stores `email_verified = 0`). Errors:
 * 400 invalid_email / invalid_username / weak_password / spam_detected /
 * disposable_email / invalid_email_domain, 403 signup_disabled (instance
 * closed), 409 email_taken / username_taken, 429 too_many_requests. */
export async function apiSignup(payload: SignupPayload): Promise<{ ok: boolean; verification_required: boolean }> {
  return fetchJSON(`${API_BASE}/auth/signup`, {
    method: 'POST', body: JSON.stringify(payload),
  });
}

/** Consume a verification token (48 h, single use). On success the server sets
 * `email_verified = 1` AND creates the session, so the caller is signed in.
 * Error: 400 invalid_token. */
export async function apiVerifyEmail(token: string): Promise<{ user: AuthUser }> {
  const data = await fetchJSON(`${API_BASE}/auth/verify-email`, {
    method: 'POST', body: JSON.stringify({ token }),
  });
  // A fresh session means queued offline mutations can now reach the server.
  void flushPending();
  return data;
}

/** Ask for a new verification e-mail. Always resolves (anti-enumeration: the
 * endpoint never reveals whether the address exists). Error: 429. */
export async function apiResendVerification(email: string): Promise<{ ok: boolean }> {
  return fetchJSON(`${API_BASE}/auth/resend-verification`, {
    method: 'POST', body: JSON.stringify({ email }),
  });
}

export type TrialState = 'none' | 'active' | 'expired';

export interface TrialStatus {
  /** 'none' = no trial row (historical account, never blocked). */
  trial: TrialState;
  daysLeft: number;
  /** ISO timestamp of the trial end, or '' when there is no trial. */
  endsAt: string;
}

/** Trial status of the signed-in user (session required). */
export async function apiGetTrialStatus(): Promise<TrialStatus> {
  return fetchJSON(`${API_BASE}/auth/trial`);
}

// ---- GDPR data-portability export ----
// Downloads the user's full data as a JSON blob. Uses a raw fetch (not
// fetchJSON) so the response is never cached in the offline store and the file
// arrives as an authentic Blob for the browser download.
export async function apiExportMyData(): Promise<Blob> {
  const res = await fetch(`${API_BASE}/account/export`, { credentials: 'include' });
  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try { const e = await res.json(); if (e?.error) msg = e.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.blob();
}

// ---- GDPR right to erasure (DELETE /api/account) ----
// Permanently deletes the signed-in user's own account + bound personal data.
export async function apiDeleteMyAccount(): Promise<{ deleted: boolean }> {
  return fetchJSON(`${API_BASE}/account`, { method: 'DELETE' });
}

// ---- Active sessions / remote logout (GET+DELETE /api/account/sessions) ----
// Lists the signed-in user's own sessions ("devices"). The raw token is never
// returned — only a masked display form and a non-reversible id used to revoke.
export interface AccountSession {
  id: string;          // sha256(token) — used only to address the DELETE url
  token_masked: string;
  device: string;
  created_at: string;
  expire: number;
  this_device: boolean;
}

export async function apiGetSessions(): Promise<AccountSession[]> {
  const data = await fetchJSON(`${API_BASE}/account/sessions`);
  return (data?.sessions || []) as AccountSession[];
}

export async function apiRevokeSession(id: string): Promise<{ revoked: boolean }> {
  return fetchJSON(`${API_BASE}/account/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function apiRevokeOtherSessions(): Promise<{ revoked: boolean }> {
  return fetchJSON(`${API_BASE}/account/sessions`, { method: 'DELETE' });
}

// ---- Audit ----
export async function apiGetAuditLog(
  limit = 200, offset = 0, action = '', username = '',
): Promise<AuditEntry[]> {
  const q = new URLSearchParams();
  q.set('limit', String(limit));
  q.set('offset', String(offset));
  if (action) q.set('action', action);
  if (username) q.set('username', username);
  return fetchJSON(`${API_BASE}/audit-log?${q.toString()}`);
}

// ---- Identity-document SCANS (passport / visa images & PDFs) — 2026-10-07 ----
// A scan is ENCRYPTED at rest and served ONLY by a guarded route, so:
//   • the URL is always fetched/inlined WITH the session cookie (same-origin
//     `<img src>` sends it; a new tab keeps it) — never a public /static path;
//   • uploads/deletes go through raw `fetch` like `apiUploadImage`, NOT
//     `fetchJSON` (a binary body must not enter the offline replay queue);
//   • nothing is cached: the route answers `Cache-Control: private, no-store`.
export type DocScanKind = 'passport' | 'visa';
export interface DocScanTarget { memberId?: string; self?: boolean }

export function docScanUrl(kind: DocScanKind, docId: string, target: DocScanTarget = {}): string {
  const base = target.self ? `${API_BASE}/me/team/documents` : `${API_BASE}/team/${target.memberId}/documents`;
  return `${base}/${kind}/${docId}/scan`;
}

async function docScanFetch(method: 'POST' | 'DELETE', kind: DocScanKind, docId: string, target: DocScanTarget, body?: unknown) {
  const res = await fetch(docScanUrl(kind, docId, target), {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j.error || j.detail || ''; } catch { /* not json */ }
    throw new Error(detail || `Scan upload failed (${res.status})`);
  }
  return res.json();
}

export interface DocScanUpload { dataUrl?: string; base64?: string; mime?: string }

export async function apiUploadDocScan(kind: DocScanKind, docId: string, data: DocScanUpload, target: DocScanTarget = {}): Promise<{ scan_path: string; size: number }> {
  return docScanFetch('POST', kind, docId, target, data);
}

export async function apiDeleteDocScan(kind: DocScanKind, docId: string, target: DocScanTarget = {}): Promise<{ deleted: boolean }> {
  return docScanFetch('DELETE', kind, docId, target);
}

// ---- Uploads ----
export async function apiUploadImage(dataUrl: string): Promise<{ url: string }> {
  const res = await fetch(`${API_BASE}/uploads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ dataUrl }),
  });
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j.error || j.detail || ''; } catch { /* ignore */ }
    throw new Error(`Upload failed (${res.status})${detail ? ': ' + detail : ''}`);
  }
  return res.json();
}
